import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { Button, CustomInput, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import Widget from '../../../components/Widget/Widget';
import {
    canAccessPayoutSettings,
    canConfigureMerchantReserve,
    canConfigureTipPayoutRecipient as canConfigureTipPayoutRecipientFn,
    canExecuteTipPayoutToRecipient as canExecuteTipPayoutToRecipientFn,
    canMerchantBankPayout,
    canViewMerchantBalanceDetails as canViewMerchantBalanceDetailsFn,
    canViewTipPayoutSettings as canViewTipPayoutSettingsFn,
    canViewTipSummaryReadOnly as canViewTipSummaryReadOnlyFn,
    buildAuthRoleState,
    isRebelityPlatformAdmin,
    shouldUseSimplifiedPayoutDashboard,
    PORTAL_PAYOUT_PATH,
} from '../../../actions/auth';
import { getTipFundingSummaryRequest } from '../../../actions/users';
import {
    loadMerchantPayoutPreviewRequest,
    loadMerchantReserveConfigRequest,
    saveMerchantReserveConfigRequest,
    createMerchantPayoutRequest,
    clearMerchantPayoutError,
    saveMerchantTipPayoutSettingsRequest,
    payReservedTipsToRecipientRequest,
    markAllOutstandingTipsPaidRequest,
    loadDirectTipPayoutRecipientsRequest,
    payDirectTipsToEmployeeRequest,
    loadMerchantTipPayoutDesignatedRecipientsRequest,
    loadMerchantTipPayoutManagerSummaryRequest,
    loadMerchantTipPayoutPendingBreakdownRequest,
    loadMerchantTipPayoutPaidToManagerBreakdownRequest,
    loadMerchantTipPayoutReleasableBreakdownRequest,
    loadRecentSuccessfulTipPayoutsRequest,
    loadRecentMerchantBankPayoutsRequest,
    RECENT_MERCHANT_BANK_PAYOUTS_PAGE_SIZE,
    adminForceReclaimRequest,
    loadForceReclaimAuditRequest,
} from '../../../actions/merchantPayout';
import { recordStripeTaxRemittanceRequest } from '../../../actions/settings';

import s from './PayoutSettings.module.scss';
import { withTranslation } from '../../../i18n/LanguageContext';
import { PAYOUT_PATH_MANAGER } from '../../../utils/tipPayoutPaidStatus';
import { formatApiDateTime } from '../../../utils/merchantLocalDateTime';
import {
    TIP_PAYOUT_DATE_PRESET_ALL,
    TIP_PAYOUT_DATE_PRESET_CUSTOM,
    TIP_PAYOUT_DATE_PRESET_WEEK,
    TIP_PAYOUT_DATE_PRESETS,
    buildTipPayoutDateRangeFromState,
    buildTipPayoutDateRangeKey,
    getPresetDateRange,
    getTodayLocalDateString,
} from '../../../utils/tipPayoutDateFilter';

const EMPTY_RESERVE_FORM = {
    flatReserveEnabled: false,
    flatReserveAmount: '',
    percentReserveEnabled: false,
    percentReserveValue: '',
    reserveTipsEnabled: false,
    reserveTaxEnabled: false,
};

const TIP_PAYOUT_MODE_MANAGER = 'manager';
const TIP_PAYOUT_MODE_EMPLOYEE = 'employee';
const PAYOUT_SETTINGS_PATH = '/admin/settings/payout';

class PayoutSettings extends React.Component {

    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        merchant: PropTypes.object,
        storeDetails: PropTypes.object,
        preview: PropTypes.object,
        previewLoading: PropTypes.bool,
        previewError: PropTypes.string,
        reserveSaving: PropTypes.bool,
        payoutCreating: PropTypes.bool,
        payoutError: PropTypes.string,
        tipFundingSummary: PropTypes.object,
        tipFundingSummaryLoading: PropTypes.bool,
        tipFundingSummaryError: PropTypes.string,
        identityRolesLoaded: PropTypes.bool,
        isRebelityAdmin: PropTypes.bool,
        useSimplifiedPayoutDashboard: PropTypes.bool,
        canMerchantBankPayout: PropTypes.bool,
        canViewMerchantBalanceDetails: PropTypes.bool,
        canConfigureReserve: PropTypes.bool,
        canViewTipPayoutSettings: PropTypes.bool,
        canViewTipSummaryReadOnly: PropTypes.bool,
        canConfigureTipPayoutRecipient: PropTypes.bool,
        canExecuteTipPayoutToRecipient: PropTypes.bool,
        tipPayoutSettings: PropTypes.object,
        tipPayoutSettingsLoading: PropTypes.bool,
        tipPayoutSettingsError: PropTypes.string,
        tipPayoutSettingsSaving: PropTypes.bool,
        tipPayoutPaying: PropTypes.bool,
        tipPayoutPayError: PropTypes.string,
        tipPayoutMarkAllLoading: PropTypes.bool,
        tipPayoutMarkAllError: PropTypes.string,
        tipPayoutDirectRecipients: PropTypes.array,
        tipPayoutDirectRecipientsLoading: PropTypes.bool,
        tipPayoutDirectRecipientsError: PropTypes.string,
        tipPayoutDirectPaying: PropTypes.bool,
        tipPayoutDirectPayError: PropTypes.string,
        tipPayoutDirectPayingUserId: PropTypes.number,
        tipPayoutDesignatedRecipientsLoading: PropTypes.bool,
        tipPayoutDesignatedRecipientsError: PropTypes.string,
        tipPayoutPendingBreakdownLoading: PropTypes.bool,
        tipPayoutPendingBreakdownError: PropTypes.string,
        tipPayoutReleasableBreakdownLoading: PropTypes.bool,
        tipPayoutReleasableBreakdownError: PropTypes.string,
        recentSuccessfulTipPayouts: PropTypes.array,
        recentSuccessfulTipPayoutsLoading: PropTypes.bool,
        recentSuccessfulTipPayoutsError: PropTypes.string,
        recentMerchantBankPayouts: PropTypes.array,
        recentMerchantBankPayoutsLoading: PropTypes.bool,
        recentMerchantBankPayoutsError: PropTypes.string,
        tipPayoutSummaryCache: PropTypes.object,
    };

    static defaultProps = {
        merchant: null,
        storeDetails: null,
        preview: null,
        previewLoading: false,
        previewError: '',
        reserveSaving: false,
        payoutCreating: false,
        payoutError: '',
        tipFundingSummary: null,
        tipFundingSummaryLoading: false,
        tipFundingSummaryError: '',
        identityRolesLoaded: false,
        isRebelityAdmin: false,
        useSimplifiedPayoutDashboard: false,
        canMerchantBankPayout: false,
        canViewMerchantBalanceDetails: false,
        canConfigureReserve: false,
        canViewTipPayoutSettings: false,
        canViewTipSummaryReadOnly: false,
        canConfigureTipPayoutRecipient: false,
        canExecuteTipPayoutToRecipient: false,
        tipPayoutSettings: null,
        tipPayoutSettingsLoading: false,
        tipPayoutSettingsError: '',
        tipPayoutSettingsSaving: false,
        tipPayoutPaying: false,
        tipPayoutPayError: '',
        tipPayoutMarkAllLoading: false,
        tipPayoutMarkAllError: '',
        tipPayoutDirectRecipients: null,
        tipPayoutDirectRecipientsLoading: false,
        tipPayoutDirectRecipientsError: '',
        tipPayoutDirectPaying: false,
        tipPayoutDirectPayError: '',
        tipPayoutDirectPayingUserId: null,
        tipPayoutDesignatedRecipientsLoading: false,
        tipPayoutDesignatedRecipientsError: '',
        tipPayoutPendingBreakdownLoading: false,
        tipPayoutPendingBreakdownError: '',
        tipPayoutReleasableBreakdownLoading: false,
        tipPayoutReleasableBreakdownError: '',
        recentSuccessfulTipPayouts: [],
        recentSuccessfulTipPayoutsLoading: false,
        recentSuccessfulTipPayoutsError: '',
        recentMerchantBankPayouts: [],
        recentMerchantBankPayoutsLoading: false,
        recentMerchantBankPayoutsError: '',
        tipPayoutSummaryCache: {},
    };

    state = {
        reserveForm: { ...EMPTY_RESERVE_FORM },
        reserveFormDirty: false,
        payoutConfirm: null,
        payoutSuccess: null,
        recentBankPayoutsOpen: true,
        recentBankPayoutsPage: 0,
        taxCollectedResetConfirmOpen: false,
        taxCollectedResetBusy: false,
        releaseReserveConfirmOpen: false,
        payTipsConfirmOpen: false,
        markAllPaidConfirmOpen: false,
        markAllPaidReason: 'All employee tips have been paid outside the system.',
        payRecipientConfirm: null,
        pendingTipsBreakdownOpen: false,
        paidToManagerBreakdownOpen: false,
        releasableTipsBreakdownOpen: false,
        payingReleasableRowKey: null,
        releasablePartialPayNote: '',
        employeeWorkPeriodModalRow: null,
        designatedRecipientDraft: '',
        tipPayoutViewMode: TIP_PAYOUT_MODE_MANAGER,
        tipPayoutViewModeDirty: false,
        tipPayoutDatePreset: TIP_PAYOUT_DATE_PRESET_ALL,
        tipPayoutCustomStartDate: getPresetDateRange(TIP_PAYOUT_DATE_PRESET_WEEK).startDate,
        tipPayoutCustomEndDate: getTodayLocalDateString(),
        forceReclaimTransferIds: '',
        forceReclaimUserId: '',
        forceReclaimWorkPeriodId: '',
        forceReclaimReason: '',
        forceReclaimPreview: null,
        forceReclaimPreviewError: '',
        forceReclaimRunning: false,
        forceReclaimConfirmOpen: false,
        forceReclaimAudit: null,
        forceReclaimAuditOpen: false,
    };

    componentDidMount() {
        if (this.canAccessPayoutPage()) {
            this.loadPageData();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.identityRolesLoaded && this.props.identityRolesLoaded && this.canAccessPayoutPage()) {
            this.loadPageData();
        }

        if (prevProps.preview !== this.props.preview && this.props.preview?.config && !this.state.reserveFormDirty) {
            this.syncReserveFormFromPreview(this.props.preview);
        }

        if (prevProps.tipPayoutSettings !== this.props.tipPayoutSettings && this.props.tipPayoutSettings) {
            this.syncDesignatedRecipientDraft(this.props.tipPayoutSettings);
        }

        if ((prevProps.reserveConfig !== this.props.reserveConfig
            || prevProps.preview !== this.props.preview)
            && !this.state.reserveFormDirty) {
            this.syncSettingsUiFromProps();
        }

        if (prevProps.preview !== this.props.preview && this.props.preview) {
            const previewJustLoaded = !prevProps.preview;
            if (previewJustLoaded && this.state.tipPayoutViewMode === TIP_PAYOUT_MODE_MANAGER) {
                window.setTimeout(() => {
                    if (this.pendingSummaryAfterPreview) {
                        this.flushPendingManagerSummary();
                    } else if (!this.hasLoadedSummaryForCurrentFilter()) {
                        this.dispatchManagerSummaryIfNeeded({ allowWhileLoading: true });
                    }
                }, 500);
            }
        }

        if (this.pendingSummaryAfterPreview
            && prevProps.previewLoading
            && !this.props.previewLoading
            && this.props.preview) {
            this.flushPendingManagerSummary();
        }
    }

    pendingSummaryAfterPreview = false;

    shouldWaitForPreviewBeforeSummary = () => {
        if (this.props.canViewTipSummaryReadOnly) {
            return false;
        }

        return !this.props.preview && this.props.previewLoading;
    };

    dispatchManagerSummaryIfNeeded = ({ force = false, allowWhileLoading = false } = {}) => {
        if (!this.canAccessTipPayoutData()) {
            return false;
        }

        if (this.state.tipPayoutViewMode !== TIP_PAYOUT_MODE_MANAGER) {
            return false;
        }

        const needsSummary = force || !this.hasLoadedSummaryForCurrentFilter();

        if (!needsSummary) {
            return false;
        }

        if (!force && !allowWhileLoading && this.props.tipPayoutSettingsLoading) {
            return false;
        }

        this.props.dispatch(loadMerchantTipPayoutManagerSummaryRequest(this.getTipPayoutDateRange()));
        return true;
    };

    ensureDesignatedRecipientsLoaded = ({ force = false } = {}) => {
        if (!this.canAccessTipPayoutData()) {
            return false;
        }

        const hasRecipients = (this.props.tipPayoutSettings?.eligibleRecipients || []).length > 0;
        if (!force && (hasRecipients || this.props.tipPayoutDesignatedRecipientsLoading)) {
            return false;
        }

        this.props.dispatch(loadMerchantTipPayoutDesignatedRecipientsRequest(this.getTipPayoutDateRange()));
        return true;
    };

    flushPendingManagerSummary = () => {
        this.pendingSummaryAfterPreview = false;
        this.dispatchManagerSummaryIfNeeded({ allowWhileLoading: true });
    };

    canSwitchTipPayoutViewMode() {
        return this.props.canExecuteTipPayoutToRecipient;
    }

    handleTipPayoutViewModeChange = (mode) => {
        if (!this.canSwitchTipPayoutViewMode()) {
            return;
        }

        this.setState({
            tipPayoutViewMode: mode,
            tipPayoutViewModeDirty: true,
        }, () => {
            this.ensureTipPayoutDataForMode(mode, { force: true });
        });
    };

    syncDesignatedRecipientDraft(settings) {
        const userId = settings?.designatedTipPayoutUserId;
        this.setState({
            designatedRecipientDraft: userId ? String(userId) : '',
        });
    }

    syncSettingsUiFromProps() {
        const config = this.props.reserveConfig || this.props.preview?.config;
        if (config && !this.state.reserveFormDirty) {
            this.syncReserveFormFromConfig(config);
        }

        if (this.props.tipPayoutSettings) {
            this.syncDesignatedRecipientDraft(this.props.tipPayoutSettings);
        }
    }

    syncReserveFormFromConfig(config) {
        if (!config) {
            return;
        }

        this.setState({
            reserveForm: {
                flatReserveEnabled: !!config.flatReserveEnabled,
                flatReserveAmount: config.flatReserveEnabled ? String(config.flatReserveAmount ?? '') : '',
                percentReserveEnabled: !!config.percentReserveEnabled,
                percentReserveValue: config.percentReserveEnabled ? String(config.percentReserveValue ?? '') : '',
                reserveTipsEnabled: !!config.reserveTipsEnabled,
                reserveTaxEnabled: !!config.reserveTaxEnabled,
            },
            reserveFormDirty: false,
        });
    }

    syncReserveFormFromPreview(preview) {
        this.syncReserveFormFromConfig(preview?.config);
    }

    loadPageData = () => {
        const tipPayoutsDisabled = this.isTipPayoutDisabled();
        if (this.props.canViewTipSummaryReadOnly) {
            if (!tipPayoutsDisabled) {
                this.props.dispatch(getTipFundingSummaryRequest());
                this.ensureTipPayoutDataForMode(TIP_PAYOUT_MODE_MANAGER, { force: true });
                this.props.dispatch(loadRecentSuccessfulTipPayoutsRequest(undefined, { backfill: true }));
            }
            return;
        }

        this.props.dispatch(loadMerchantPayoutPreviewRequest());
        if (this.props.canConfigureReserve) {
            this.props.dispatch(loadMerchantReserveConfigRequest());
        }
        if (this.props.canMerchantBankPayout) {
            this.props.dispatch(loadRecentMerchantBankPayoutsRequest());
        }
        this.props.dispatch(getTipFundingSummaryRequest());
        if (!tipPayoutsDisabled && this.props.canViewTipPayoutSettings) {
            this.ensureDesignatedRecipientsLoaded({ force: true });
            this.ensureTipPayoutDataForMode(this.state.tipPayoutViewMode);
            this.props.dispatch(loadRecentSuccessfulTipPayoutsRequest(undefined, { backfill: true }));
        }

        window.setTimeout(() => this.syncSettingsUiFromProps(), 0);
    };

    getTipPayoutDateRange = () => buildTipPayoutDateRangeFromState(this.state);

    hasActiveTipPayoutDateFilter = () => {
        const range = this.getTipPayoutDateRange();
        return !!(range?.startDate || range?.endDate);
    };

    getTipPayoutSummaryDateRangeKey = () => buildTipPayoutDateRangeKey(this.getTipPayoutDateRange());

    hasLoadedSettingsSummaryForCurrentFilter = () => {
        const { tipPayoutSettings, tipPayoutSettingsDateRangeKey, tipPayoutSettingsLoading } = this.props;
        if (!tipPayoutSettings || tipPayoutSettingsDateRangeKey !== this.getTipPayoutSummaryDateRangeKey()) {
            return false;
        }

        if (tipPayoutSettingsLoading) {
            return false;
        }

        return tipPayoutSettings.releasableTipsAndGratuity !== undefined
            || tipPayoutSettings.pendingTipsAndGratuity !== undefined;
    };

    hasLoadedSummaryForCurrentFilter = () => this.hasLoadedSettingsSummaryForCurrentFilter();

    getCachedManagerSummaryAmounts() {
        const cacheKey = this.getTipPayoutSummaryDateRangeKey();
        const cached = this.props.tipPayoutSummaryCache?.[cacheKey];
        if (!cached || cached.summaryVersion !== 6) {
            return null;
        }

        const available = toNumber(cached.releasableTipsAndGratuity);
        const pending = toNumber(cached.pendingTipsAndGratuity);
        const paidToManager = toNumber(cached.tipsAndGratuityPaidToManager);
        return {
            available,
            pending,
            paidToManager,
            totalUnpaid: available + pending + paidToManager,
        };
    }

    getManagerSummaryAmounts(
        tipPayoutSettings = this.props.tipPayoutSettings,
        preview = this.props.preview,
    ) {
        const routingMode = tipPayoutSettings?.tipPayoutRoutingMode || preview?.tipPayoutRoutingMode;
        const isPlatform = this.isPlatformTipRouting(routingMode);
        let amounts = null;
        const cached = this.getCachedManagerSummaryAmounts();
        if (cached) {
            amounts = cached;
        } else if (this.hasLoadedSettingsSummaryForCurrentFilter()) {
            const available = toNumber(tipPayoutSettings.releasableTipsAndGratuity);
            const pending = toNumber(tipPayoutSettings.pendingTipsAndGratuity);
            const paidToManager = isPlatform
                ? toNumber(tipPayoutSettings.tipsAndGratuityPaidToManager)
                : 0;
            amounts = {
                available,
                pending,
                paidToManager,
                totalUnpaid: isPlatform
                    ? available + pending + paidToManager
                    : available + pending,
            };
        } else if (!this.hasActiveTipPayoutDateFilter() && preview && !this.props.tipPayoutSettingsLoading) {
            const isFastPreview = preview.previewDetailLevel === 'fast'
                && preview.releasableTipsAndGratuity == null
                && preview.pendingTipsAndGratuity == null;
            if (!isFastPreview) {
                amounts = this.getTipsGratuityAmountsFromPreview(preview);
            }
        }

        if (!amounts) {
            amounts = { available: 0, pending: 0, paidToManager: 0, totalUnpaid: 0 };
        } else if (amounts.paidToManager == null) {
            amounts = {
                ...amounts,
                paidToManager: isPlatform
                    ? toNumber(tipPayoutSettings?.tipsAndGratuityPaidToManager)
                    : 0,
            };
            if (isPlatform) {
                amounts.totalUnpaid = toNumber(amounts.available)
                    + toNumber(amounts.pending)
                    + toNumber(amounts.paidToManager);
            }
        }

        return this.applySettledBalanceCoverRemap(preview, amounts, { isPlatform });
    }

    shouldShowManagerTotalUnpaidRow(totalUnpaid) {
        return toNumber(totalUnpaid) > 0;
    }

    getTipsGratuityAmountsFromPreview(preview = this.props.preview) {
        if (!preview) {
            return { available: 0, pending: 0, totalUnpaid: 0 };
        }

        const totalUnpaid = this.getTotalTipsGratuityHeldAmount(preview);
        const hasBackendSplit = preview.releasableTipsAndGratuity !== undefined
            && preview.releasableTipsAndGratuity !== null
            && preview.pendingTipsAndGratuity !== undefined
            && preview.pendingTipsAndGratuity !== null;

        if (hasBackendSplit) {
            const available = toNumber(preview.releasableTipsAndGratuity);
            const pending = toNumber(preview.pendingTipsAndGratuity);
            const scopedTotalUnpaid = available + pending > 0 ? available + pending : totalUnpaid;
            return this.applySettledBalanceCoverRemap(preview, {
                available,
                pending,
                totalUnpaid: scopedTotalUnpaid,
            });
        }

        const liquidity = preview.config?.reserveTipsEnabled
            ? toNumber(preview.tipsReserveAmount)
            : toNumber(preview.releasableTipsAndGratuity ?? preview.availableBalance ?? preview.totalReserved);
        const available = Math.min(Math.max(0, liquidity), Math.max(0, totalUnpaid));
        const pending = Math.max(0, totalUnpaid - available);
        return this.applySettledBalanceCoverRemap(preview, { available, pending, totalUnpaid });
    };

    isManagerSummaryLoading() {
        if (this.hasLoadedSettingsSummaryForCurrentFilter()) {
            return false;
        }

        if (this.getCachedManagerSummaryAmounts()) {
            return false;
        }

        if (this.props.tipPayoutSettingsLoading) {
            return true;
        }

        if (!this.hasActiveTipPayoutDateFilter()) {
            if (this.props.canViewTipSummaryReadOnly) {
                return this.props.tipPayoutSettingsLoading && !this.hasLoadedSettingsSummaryForCurrentFilter();
            }

            return !this.props.preview && this.props.previewLoading;
        }

        return true;
    }

    refetchTipPayoutDataForCurrentMode = () => {
        if (!this.canAccessTipPayoutData()) {
            return;
        }

        const dateRange = this.getTipPayoutDateRange();
        const mode = this.state.tipPayoutViewMode;

        if (mode === TIP_PAYOUT_MODE_MANAGER) {
            if (this.shouldWaitForPreviewBeforeSummary()) {
                this.pendingSummaryAfterPreview = true;
                return;
            }

            this.pendingSummaryAfterPreview = false;
            this.dispatchManagerSummaryIfNeeded({ allowWhileLoading: true });
            return;
        }

        this.props.dispatch(loadDirectTipPayoutRecipientsRequest(dateRange));
    };

    handleTipPayoutDatePresetChange = (preset) => {
        if (preset === this.state.tipPayoutDatePreset) {
            return;
        }

        this.setState({ tipPayoutDatePreset: preset }, () => {
            if (preset === TIP_PAYOUT_DATE_PRESET_CUSTOM) {
                const range = this.getTipPayoutDateRange();
                if (range?.startDate && range?.endDate) {
                    this.refetchTipPayoutDataForCurrentMode();
                }
                return;
            }

            this.refetchTipPayoutDataForCurrentMode();
        });
    };

    handleTipPayoutCustomStartDateChange = (event) => {
        this.setState({ tipPayoutCustomStartDate: event.target.value });
    };

    handleTipPayoutCustomEndDateChange = (event) => {
        this.setState({ tipPayoutCustomEndDate: event.target.value });
    };

    applyTipPayoutCustomDateRange = () => {
        const { tipPayoutCustomStartDate, tipPayoutCustomEndDate } = this.state;
        if (tipPayoutCustomStartDate && tipPayoutCustomEndDate && tipPayoutCustomStartDate > tipPayoutCustomEndDate) {
            return;
        }

        this.refetchTipPayoutDataForCurrentMode();
    };

    ensureTipPayoutDataForMode = (mode, { force = false } = {}) => {
        if (!this.canAccessTipPayoutData()) {
            return;
        }

        const dateRange = this.getTipPayoutDateRange();

        if (mode === TIP_PAYOUT_MODE_MANAGER) {
            if (force) {
                this.ensureDesignatedRecipientsLoaded({ force: true });
            }

            const needsSummary = force || !this.hasLoadedSummaryForCurrentFilter();
            if (!needsSummary) {
                return;
            }

            if (!force && this.shouldWaitForPreviewBeforeSummary()) {
                this.pendingSummaryAfterPreview = true;
                return;
            }

            this.pendingSummaryAfterPreview = false;
            this.props.dispatch(loadMerchantTipPayoutManagerSummaryRequest(dateRange));
            return;
        }

        if (force || (this.props.tipPayoutDirectRecipients === null && !this.props.tipPayoutDirectRecipientsLoading)) {
            this.props.dispatch(loadDirectTipPayoutRecipientsRequest(dateRange));
        }
    };

    hasTipPayoutManagerSummary = () => {
        const settings = this.props.tipPayoutSettings;
        if (!settings) {
            return false;
        }

        return settings.releasableTipsAndGratuity !== undefined
            || settings.pendingTipsAndGratuity !== undefined
            || settings.reservedTipsAndGratuity !== undefined;
    };

    canViewPayoutSettings() {
        return canAccessPayoutSettings(this.props);
    }

    canAccessPayoutPage() {
        return this.canViewPayoutSettings() || !!this.props.canViewTipSummaryReadOnly;
    }

    getTipPayoutRoutingMode() {
        return this.props.preview?.tipPayoutRoutingMode
            || this.props.tipPayoutSettings?.tipPayoutRoutingMode
            || this.props.merchant?.TipPayoutRoutingMode
            || this.props.storeDetails?.TipPayoutRoutingMode;
    }

    isTipPayoutDisabled() {
        const normalized = String(this.getTipPayoutRoutingMode() ?? "").toLowerCase();
        return normalized === "disabled" || normalized === "3";
    }

    canAccessTipPayoutData() {
        return !this.isTipPayoutDisabled()
            && (!!this.props.canViewTipPayoutSettings || !!this.props.canViewTipSummaryReadOnly);
    }

    getReadOnlyTotalUnpaidTips(tipPayoutSettings = this.props.tipPayoutSettings) {
        const { totalUnpaid } = this.getManagerSummaryAmounts(tipPayoutSettings, null);
        return toNumber(totalUnpaid);
    }

    getFundingNumber(summary, ...keys) {
        if (!summary) return 0;
        for (const key of keys) {
            const value = summary[key];
            if (value !== undefined && value !== null && !Number.isNaN(Number(value))) {
                return Number(value);
            }
        }
        return 0;
    }

    isPlatformLiquidityDisplayPending() {
        if (!isRebelityPlatformAdmin(this.props)) {
            return false;
        }

        const { tipFundingSummary, tipFundingSummaryLoading } = this.props;
        return tipFundingSummaryLoading && !tipFundingSummary;
    }

    isPlatformBalanceLiquid() {
        const { tipFundingSummary } = this.props;
        if (!tipFundingSummary) {
            return false;
        }

        const platformAvailable = this.getFundingNumber(
            tipFundingSummary,
            'PlatformAvailable',
            'platformAvailable',
        );
        const openObligations = this.getFundingNumber(
            tipFundingSummary,
            'OpenObligationAmount',
            'openObligationAmount',
        );

        return platformAvailable + 0.001 >= openObligations;
    }

    merchantSettledBalanceCoversTotalUnpaid(preview = this.props.preview, totalUnpaid = 0) {
        if (!preview || toNumber(totalUnpaid) <= 0.001) {
            return false;
        }

        return toNumber(preview.availableBalance) + 0.001 >= toNumber(totalUnpaid);
    }

    /**
     * When Settled Balance covers held unpaid, lift liquidity-gated Pending into Available.
     * Do not remap "paid to manager" residual into Available — that is not releasable again.
     */
    applySettledBalanceCoverRemap(preview, amounts, { isPlatform = false } = {}) {
        const available = toNumber(amounts?.available);
        const pending = toNumber(amounts?.pending);
        const paidToManager = toNumber(amounts?.paidToManager);
        const totalUnpaid = isPlatform
            ? available + pending + paidToManager
            : toNumber(amounts?.totalUnpaid) || (available + pending);

        if (!this.merchantSettledBalanceCoversTotalUnpaid(preview, available + pending)) {
            return {
                available,
                pending,
                paidToManager,
                totalUnpaid,
            };
        }

        // Settled balance covers held obligations (available + pending held). Keep paidToManager aside.
        const heldUnpaid = available + pending;
        if (pending > 0.001) {
            return {
                available: heldUnpaid,
                pending: 0,
                paidToManager,
                totalUnpaid: heldUnpaid + paidToManager,
            };
        }

        return {
            available: heldUnpaid,
            pending: 0,
            paidToManager,
            totalUnpaid: heldUnpaid + paidToManager,
        };
    }

    shouldShowMerchantTipsGratuityLiquidity() {
        if (this.isPlatformLiquidityDisplayPending()) {
            return false;
        }

        return !this.isPlatformBalanceLiquid();
    }

    shouldShowMerchantSplitInTipPayoutSummary(
        tipPayoutSettings = this.props.tipPayoutSettings,
        preview = this.props.preview,
    ) {
        return !this.isPlatformLiquidityDisplayPending();
    }

    formatCurrency = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '$0.00';
        return `$${parseFloat(value).toFixed(2)}`;
    };

    formatTaxCollectedSinceDate = (preview = this.props.preview) => {
        const raw = preview?.taxCollectedSinceLocalDate ?? preview?.TaxCollectedSinceLocalDate;
        if (raw) {
            const parsed = new Date(raw);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                });
            }
        }
        // Fallback matches server tax-reserve start (merchant-local calendar date).
        return 'August 8, 2026';
    };

    openTaxCollectedResetConfirm = () => {
        const amount = toNumber(this.props.preview?.taxCollectedAmount);
        if (amount <= 0 || this.state.taxCollectedResetBusy) {
            return;
        }
        this.setState({ taxCollectedResetConfirmOpen: true });
    };

    closeTaxCollectedResetConfirm = () => {
        if (this.state.taxCollectedResetBusy) {
            return;
        }
        this.setState({ taxCollectedResetConfirmOpen: false });
    };

    confirmTaxCollectedReset = async () => {
        const amount = toNumber(this.props.preview?.taxCollectedAmount);
        if (amount <= 0 || this.state.taxCollectedResetBusy) {
            return;
        }

        this.setState({ taxCollectedResetBusy: true });
        try {
            await this.props.dispatch(recordStripeTaxRemittanceRequest({
                Amount: amount,
                Reference: `tax-collected-reset-${Date.now()}`,
                Notes: 'Reset Tax Collected to $0 from Payout Settings Reserve dashboard.',
            }));
            this.setState({
                taxCollectedResetConfirmOpen: false,
                taxCollectedResetBusy: false,
            });
            await this.props.dispatch(loadMerchantPayoutPreviewRequest({ full: true }));
            if (this.props.canConfigureReserve) {
                this.props.dispatch(loadMerchantReserveConfigRequest());
            }
        } catch (err) {
            this.setState({ taxCollectedResetBusy: false });
        }
    };

    formatPayoutDateTime = (value) => {
        return this.formatWorkPeriodDate(value);
    };

    formatStripeTransferId = (transferId) => {
        const normalized = String(transferId || '').trim();
        if (!normalized) {
            return '—';
        }

        if (normalized.length <= 14) {
            return normalized;
        }

        return `${normalized.slice(0, 10)}…${normalized.slice(-4)}`;
    };

    getTotalTipsGratuityHeldAmount(preview = this.props.preview) {
        return toNumber(preview?.totalUnpaidTipsAndGratuity ?? preview?.unpaidTipAmount);
    }

    getTipsAvailableToReleaseAmount(
        preview = this.props.preview,
        tipPayoutSettings = this.props.tipPayoutSettings,
        { forManagerSummary = false } = {},
    ) {
        if (forManagerSummary) {
            return this.getManagerSummaryAmounts(tipPayoutSettings, preview).available;
        }

        return this.getTipsGratuityAmountsFromPreview(preview).available;
    }

    getTotalUnpaidTipsGratuityAmount(tipPayoutSettings = this.props.tipPayoutSettings, preview = this.props.preview) {
        // Reserve dashboard totals are merchant-wide from preview, not work-period filtered.
        return toNumber(this.getTipsGratuityAmountsFromPreview(preview).totalUnpaid);
    }

    getTipsGratuityAmounts(tipPayoutSettings = this.props.tipPayoutSettings, preview = this.props.preview) {
        return this.getTipsGratuityAmountsFromPreview(preview);
    }

    getTipsGratuityAvailableAmount(tipPayoutSettings = this.props.tipPayoutSettings) {
        return this.getTipsGratuityAmountsFromPreview(this.props.preview).available;
    }

    getTipsGratuityPendingAmount(tipPayoutSettings = this.props.tipPayoutSettings, preview = this.props.preview) {
        return this.getTipsGratuityAmountsFromPreview(preview).pending;
    }

    shouldShowTipsGratuityCard(preview = this.props.preview, tipPayoutSettings = this.props.tipPayoutSettings) {
        if (this.isTipPayoutDisabled() || !preview || this.isPlatformLiquidityDisplayPending()) {
            return false;
        }

        const reserveTipsEnabled = !!preview.config?.reserveTipsEnabled;
        const totalHeld = this.getTotalTipsGratuityHeldAmount(preview);
        const { available, pending } = this.getTipsGratuityAmountsFromPreview(preview);

        return reserveTipsEnabled || totalHeld > 0 || available > 0 || pending > 0
            || toNumber(preview?.totalReserved) > 0;
    }

    shouldShowTotalTipsHeldCard(preview = this.props.preview, tipPayoutSettings = this.props.tipPayoutSettings) {
        if (this.isTipPayoutDisabled() || !preview) {
            return false;
        }

        return this.getTotalUnpaidTipsGratuityAmount(tipPayoutSettings, preview) > 0
            || !!preview.config?.reserveTipsEnabled;
    }

    shouldShowTaxCard(preview = this.props.preview) {
        if (!preview || !isRebelityPlatformAdmin(this.props)) {
            return false;
        }

        return toNumber(preview.taxCollectedAmount) > 0
            || toNumber(preview.taxReserveAmount) > 0
            || !!preview.config?.reserveTaxEnabled;
    }

    getTipPayoutRoutingInfo(mode) {
        const normalized = String(mode || '').toLowerCase();

        if (normalized === 'merchantaccount' || normalized === '2') {
            return {
                label: 'Merchant Account',
                description: 'Tips go to the merchant connected account first. Employees are paid by reclaim, transfer, or other means.',
            };
        }

        if (normalized === 'disabled' || normalized === '3') {
            return {
                label: 'Disabled',
                description: 'Tip payouts are disabled. Unpaid tip totals are not calculated on this page. Changing routing mode starts tip payout tracking fresh and does not backfill past tips.',
            };
        }

        return {
            label: 'Platform',
            description:
                'Tips On employees receive tip/gratuity at sale time on their Stripe Connect account. '
                + 'If an employee’s Stripe is not correctly configured, sale-time tips fall back to the designated manager. '
                + 'Release Available Tips To Manager covers remaining unpaid tips.',
        };
    };

    isPlatformTipRouting(mode) {
        const normalized = String(mode || '').toLowerCase();
        if (normalized === 'merchantaccount' || normalized === '2') {
            return false;
        }
        if (normalized === 'disabled' || normalized === '3') {
            return false;
        }
        // PlatformApplicationFee / 1 / empty default treated as Platform.
        return true;
    }

    getTipsAndGratuityPaidToManagerAmount(tipPayoutSettings) {
        const cached = this.getCachedManagerSummaryAmounts();
        if (cached && cached.paidToManager != null) {
            return toNumber(cached.paidToManager);
        }
        return toNumber(tipPayoutSettings?.tipsAndGratuityPaidToManager);
    }

    handleReserveToggle = (field) => (event) => {
        const checked = event.target.checked;
        this.setState((prev) => ({
            reserveFormDirty: true,
            reserveForm: {
                ...(prev.reserveForm || EMPTY_RESERVE_FORM),
                [field]: checked,
            },
        }));
    };

    handleReserveInput = (field) => (event) => {
        const { value } = event.target;
        this.setState((prev) => ({
            reserveFormDirty: true,
            reserveForm: {
                ...(prev.reserveForm || EMPTY_RESERVE_FORM),
                [field]: value,
            },
        }));
    };

    handleSaveReserve = (event) => {
        event.preventDefault();
        const reserveForm = { ...EMPTY_RESERVE_FORM, ...this.state.reserveForm };
        const flatAmount = parseAmountInput(reserveForm.flatReserveAmount);
        const percentValue = parseAmountInput(reserveForm.percentReserveValue);

        this.props.dispatch(saveMerchantReserveConfigRequest({
            flatReserveEnabled: reserveForm.flatReserveEnabled,
            flatReserveAmount: reserveForm.flatReserveEnabled ? (flatAmount ?? 0) : 0,
            percentReserveEnabled: reserveForm.percentReserveEnabled,
            percentReserveValue: reserveForm.percentReserveEnabled ? (percentValue ?? 0) : 0,
            reserveTipsEnabled: this.isTipPayoutDisabled()
                ? !!(this.props.reserveConfig || this.props.preview?.config || {}).reserveTipsEnabled
                : reserveForm.reserveTipsEnabled,
            reserveTaxEnabled: reserveForm.reserveTaxEnabled,
        }));
        this.setState({ reserveFormDirty: false });
    };

    openReleaseReserveModal = () => {
        this.setState({ releaseReserveConfirmOpen: true });
    };

    closeReleaseReserveModal = () => {
        if (this.props.reserveSaving) return;
        this.setState({ releaseReserveConfirmOpen: false });
    };

    confirmReleaseReserve = () => {
        this.setState({
            reserveForm: { ...EMPTY_RESERVE_FORM },
            reserveFormDirty: false,
            releaseReserveConfirmOpen: false,
        });
        this.props.dispatch(saveMerchantReserveConfigRequest({ ...EMPTY_RESERVE_FORM }));
    };

    getPayoutEligibleAmount(method, preview = this.props.preview) {
        if (!preview) return 0;
        return method === 'instant'
            ? (preview.instantPayoutEligible ?? 0)
            : (preview.autoPayoutEligible ?? 0);
    }

    canOpenPayoutModal(preview = this.props.preview) {
        const standardEligible = preview?.autoPayoutEligible ?? 0;
        const instantEligible = preview?.instantPayoutEligible ?? 0;
        return standardEligible > 0 || (!!preview?.instantPayoutAvailable && instantEligible > 0);
    }

    resolveDefaultPayoutMethod(preview = this.props.preview) {
        const standardEligible = preview?.autoPayoutEligible ?? 0;
        const instantEligible = preview?.instantPayoutEligible ?? 0;
        const instantAvailable = !!preview?.instantPayoutAvailable;

        if (standardEligible > 0) {
            return 'standard';
        }
        if (instantAvailable && instantEligible > 0) {
            return 'instant';
        }
        return 'standard';
    }

    openPayoutConfirmModal = ({ method, amount } = {}) => {
        const { preview } = this.props;
        const instantAvailable = !!preview?.instantPayoutAvailable;

        let resolvedMethod = method || this.resolveDefaultPayoutMethod(preview);
        if (resolvedMethod === 'instant' && !instantAvailable) {
            resolvedMethod = this.resolveDefaultPayoutMethod(preview);
        }

        const maxAmount = this.getPayoutEligibleAmount(resolvedMethod, preview);
        const resolvedAmount = amount ?? maxAmount;

        this.props.dispatch(clearMerchantPayoutError());
        this.setState({
            payoutConfirm: {
                amountInput: resolvedAmount > 0 ? resolvedAmount.toFixed(2) : '',
                method: resolvedMethod,
            },
        });
    };

    closePayoutConfirmModal = () => {
        if (this.props.payoutCreating) return;
        this.setState({ payoutConfirm: null });
    };

    handlePayoutConfirmAmountChange = (event) => {
        const { value } = event.target;
        this.setState((prev) => {
            if (!prev.payoutConfirm) {
                return null;
            }

            return {
                payoutConfirm: {
                    method: prev.payoutConfirm.method || 'standard',
                    amountInput: value,
                },
            };
        });
    };

    handlePayoutConfirmMethodChange = (event) => {
        const method = event.target.value;
        const maxAmount = this.getPayoutEligibleAmount(method);

        this.setState((prev) => {
            if (!prev.payoutConfirm) {
                return null;
            }

            const parsed = parseAmountInput(prev.payoutConfirm.amountInput);
            let amountInput = prev.payoutConfirm.amountInput ?? '';
            if (parsed !== null && parsed > maxAmount) {
                amountInput = maxAmount > 0 ? maxAmount.toFixed(2) : '';
            }

            return {
                payoutConfirm: {
                    method,
                    amountInput,
                },
            };
        });
    };

    getPayoutConfirmAmount = () => parseAmountInput(this.state.payoutConfirm?.amountInput);

    confirmPayoutContinue = async () => {
        if (this.props.payoutCreating) {
            return;
        }

        const { preview } = this.props;
        const { payoutConfirm } = this.state;
        if (!payoutConfirm) {
            return;
        }

        const method = payoutConfirm.method ?? 'standard';
        const maxAmount = this.getPayoutEligibleAmount(method, preview);

        if (maxAmount <= 0) {
            const methodLabel = method === 'instant' ? 'instant' : 'standard';
            window.alert(`No funds are available for ${methodLabel} payout.`);
            return;
        }

        const amount = this.getPayoutConfirmAmount();
        if (amount === null || amount <= 0) {
            window.alert('Enter a payout amount greater than zero.');
            return;
        }

        if (amount > maxAmount) {
            window.alert(`Payout amount cannot exceed ${this.formatCurrency(maxAmount)}.`);
            return;
        }

        if (method === 'instant' && !preview?.instantPayoutAvailable) {
            window.alert('Instant payout is not available for this merchant account.');
            return;
        }

        const outcome = await this.props.dispatch(createMerchantPayoutRequest(amount, method));
        if (outcome?.success) {
            const result = outcome.result || {};
            const payoutMethod = String(result.payoutMethod || result.PayoutMethod || method || 'standard').toLowerCase();
            this.setState({
                payoutConfirm: null,
                payoutSuccess: {
                    amount: Number(result.amount ?? result.Amount ?? amount) || amount,
                    method: payoutMethod === 'instant' ? 'instant' : 'standard',
                    estimatedFee: Number(result.estimatedFee ?? result.EstimatedFee) || 0,
                    payoutId: result.payoutId || result.PayoutId || '',
                },
                recentBankPayoutsPage: 0,
            });
            if (this.state.recentBankPayoutsOpen) {
                this.props.dispatch(loadRecentMerchantBankPayoutsRequest());
            }
        }
    };

    closePayoutSuccessModal = () => {
        this.setState({ payoutSuccess: null });
    };

    handleOpenMerchantPayoutModal = () => {
        if (!this.canOpenPayoutModal()) {
            window.alert('No funds are available for payout.');
            return;
        }

        this.openPayoutConfirmModal();
    };

    toggleRecentBankPayouts = () => {
        const nextOpen = !this.state.recentBankPayoutsOpen;
        this.setState({ recentBankPayoutsOpen: nextOpen }, () => {
            if (nextOpen) {
                this.props.dispatch(loadRecentMerchantBankPayoutsRequest());
            }
        });
    };

    setRecentBankPayoutsPage = (page) => {
        const rows = this.props.recentMerchantBankPayouts || [];
        const pageSize = RECENT_MERCHANT_BANK_PAYOUTS_PAGE_SIZE;
        const maxPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1);
        const next = Math.max(0, Math.min(page, maxPage));
        this.setState({ recentBankPayoutsPage: next });
    };

    formatBankPayoutMethod = (method) => {
        return String(method || '').toLowerCase() === 'instant' ? 'Instant' : 'Standard';
    };

    formatBankPayoutStatus = (status) => {
        const normalized = String(status || '').trim().toLowerCase();
        if (!normalized) return 'Unknown';
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    getBankPayoutStatusClassName = (status) => {
        const normalized = String(status || '').trim().toLowerCase();
        if (normalized === 'paid' || normalized === 'completed') return s.recentTipPayoutStatusCompleted;
        if (normalized === 'pending' || normalized === 'in_transit') return s.recentTipPayoutStatusPartial;
        if (normalized === 'failed' || normalized === 'canceled' || normalized === 'cancelled') {
            return s.recentTipPayoutStatusFailed;
        }
        return s.recentTipPayoutStatusUnknown;
    };

    formatBankPayoutDateTime = (row) => {
        if (!row) return '—';
        return formatApiDateTime(row.createdAtLocal || row.createdAtUtc, '—');
    };

    renderRecentMerchantBankPayouts() {
        const {
            recentMerchantBankPayouts,
            recentMerchantBankPayoutsLoading,
            recentMerchantBankPayoutsError,
        } = this.props;
        const { recentBankPayoutsOpen, recentBankPayoutsPage } = this.state;
        const rows = recentMerchantBankPayouts || [];
        const pageSize = RECENT_MERCHANT_BANK_PAYOUTS_PAGE_SIZE;
        const totalPages = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
        const page = Math.min(recentBankPayoutsPage, totalPages - 1);
        const pageRows = rows.slice(page * pageSize, page * pageSize + pageSize);
        const showingFrom = rows.length === 0 ? 0 : page * pageSize + 1;
        const showingTo = Math.min(rows.length, page * pageSize + pageSize);

        return (
            <div className={s.recentBankPayouts}>
                <button
                    type="button"
                    className={s.recentBankPayoutsToggle}
                    onClick={this.toggleRecentBankPayouts}
                    aria-expanded={recentBankPayoutsOpen}
                >
                    <span>
                        Recent Payouts
                        {rows.length > 0 ? ` (${rows.length})` : ''}
                    </span>
                    <i className={`zmdi zmdi-chevron-${recentBankPayoutsOpen ? 'up' : 'down'}`} aria-hidden="true" />
                </button>
                {recentBankPayoutsOpen ? (
                    <div className={s.recentBankPayoutsPanel}>
                        <div className={s.sectionHeader}>
                            <p className={s.noteText}>
                                Up to 50 recent bank payouts from the merchant connected account (10 per page).
                            </p>
                            <button
                                type="button"
                                className={s.refreshBtn}
                                onClick={() => this.props.dispatch(loadRecentMerchantBankPayoutsRequest())}
                                disabled={recentMerchantBankPayoutsLoading}
                            >
                                {recentMerchantBankPayoutsLoading ? 'Refreshing…' : 'Refresh'}
                            </button>
                        </div>
                        {recentMerchantBankPayoutsError ? (
                            <p className={s.errorText}>{recentMerchantBankPayoutsError}</p>
                        ) : null}
                        {recentMerchantBankPayoutsLoading && rows.length === 0 ? (
                            <p className={s.noteText}>Loading recent payouts…</p>
                        ) : rows.length > 0 ? (
                            <>
                                <div className={s.payRecipientsTableWrap}>
                                    <table className={s.payRecipientsTable}>
                                        <thead>
                                            <tr>
                                                <th scope="col">Date</th>
                                                <th scope="col">Amount</th>
                                                <th scope="col">Method</th>
                                                <th scope="col">Status</th>
                                                <th scope="col">Stripe payout</th>
                                                <th scope="col">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pageRows.map((row) => {
                                                const isFailed = String(row.status || '').toLowerCase() === 'failed';
                                                const reason = isFailed ? (row.failureReason || '—') : '—';
                                                return (
                                                    <tr key={row.payoutId || `${row.createdAtUtc}-${row.amount}`}>
                                                        <td className={s.recentTipPayoutsMetaCell}>
                                                            {this.formatBankPayoutDateTime(row)}
                                                        </td>
                                                        <td className={s.recentTipPayoutsAmountCell}>
                                                            {this.formatCurrency(row.amount)}
                                                        </td>
                                                        <td className={s.recentTipPayoutsMetaCell}>
                                                            {this.formatBankPayoutMethod(row.method)}
                                                        </td>
                                                        <td className={s.recentTipPayoutsMetaCell}>
                                                            <span
                                                                className={`${s.recentTipPayoutStatusPill} ${this.getBankPayoutStatusClassName(row.status)}`}
                                                            >
                                                                {this.formatBankPayoutStatus(row.status)}
                                                            </span>
                                                        </td>
                                                        <td
                                                            className={s.recentTipPayoutsMetaCell}
                                                            title={row.payoutId || ''}
                                                        >
                                                            {this.formatStripeTransferId(row.payoutId)}
                                                        </td>
                                                        <td
                                                            className={s.recentBankPayoutReasonCell}
                                                            title={isFailed && row.failureReason ? row.failureReason : ''}
                                                        >
                                                            {reason}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className={s.recentBankPayoutsPager}>
                                    <span className={s.noteText}>
                                        {showingFrom}–{showingTo} of {rows.length}
                                    </span>
                                    <div className={s.recentBankPayoutsPagerBtns}>
                                        <button
                                            type="button"
                                            className={s.refreshBtn}
                                            disabled={page <= 0}
                                            onClick={() => this.setRecentBankPayoutsPage(page - 1)}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            type="button"
                                            className={s.refreshBtn}
                                            disabled={page >= totalPages - 1}
                                            onClick={() => this.setRecentBankPayoutsPage(page + 1)}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : !recentMerchantBankPayoutsLoading ? (
                            <p className={s.noteText}>No recent payouts.</p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    }

    renderStandardPayoutBlock() {
        if (!this.props.canMerchantBankPayout) {
            return null;
        }

        const { preview, payoutCreating } = this.props;
        const canPayout = this.canOpenPayoutModal(preview);

        return (
            <div className={s.payoutAvailableBlock}>
                <p className={s.payoutAvailableIntro}>
                    Pay out available non-reserved funds from the merchant connected account.
                    Choose standard or instant payout in the confirmation step.
                </p>
                <Button
                    color="primary"
                    type="button"
                    className={s.payoutBtn}
                    disabled={payoutCreating || !canPayout}
                    onClick={this.handleOpenMerchantPayoutModal}
                >
                    {payoutCreating ? 'Processing…' : 'Pay out'}
                </Button>
                {this.renderRecentMerchantBankPayouts()}
            </div>
        );
    }

    renderPreviewPanel() {
        const {
            preview,
            previewLoading,
            previewError,
            tipPayoutSettings,
            canViewMerchantBalanceDetails,
        } = this.props;
        const storeName = preview?.storeName || this.props.merchant?.StoreName || 'Current merchant';
        const instantEligible = preview?.instantPayoutEligible ?? 0;
        const instantFeePercent = toNumber(preview?.instantPayoutFeePercent, 2);
        const showFlatReserveCard = hasReserveAmount(preview?.flatReserveAmount);
        const showPercentReserveCard = hasReserveAmount(preview?.percentReserveAmount);
        const showTipsGratuityCard = this.shouldShowTipsGratuityCard(preview, tipPayoutSettings);
        const showTotalTipsHeldCard = this.shouldShowTotalTipsHeldCard(preview);
        const showTaxCard = this.shouldShowTaxCard(preview);
        const tipsPending = this.getTipsGratuityPendingAmount(tipPayoutSettings, preview);
        const tipsAvailableToRelease = this.getTipsAvailableToReleaseAmount(preview, tipPayoutSettings);
        const totalUnpaidTipsGratuity = this.getTotalUnpaidTipsGratuityAmount(tipPayoutSettings, preview);
        const taxCollectedAmount = toNumber(preview?.taxCollectedAmount);
        const taxReserveAmount = toNumber(preview?.taxReserveAmount);

        return (
            <div className={s.previewPanel}>
                <div className={s.sectionHeader}>
                    <span className={s.sectionTitle}>Reserve dashboard</span>
                    <button
                        type="button"
                        className={s.refreshBtn}
                        onClick={() => {
                            this.props.dispatch(loadMerchantPayoutPreviewRequest({ full: true }));
                            this.props.dispatch(getTipFundingSummaryRequest());
                            if (this.props.canConfigureReserve) {
                                this.props.dispatch(loadMerchantReserveConfigRequest());
                            }
                            if (!this.isTipPayoutDisabled() && this.props.canViewTipPayoutSettings) {
                                this.ensureDesignatedRecipientsLoaded({ force: true });
                                this.props.dispatch(loadMerchantTipPayoutManagerSummaryRequest(this.getTipPayoutDateRange()));
                            }
                        }}
                        disabled={previewLoading}
                    >
                        {previewLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                <p className={s.storeName}>{storeName}</p>

                {previewError && !preview ? <p className={s.errorText}>{previewError}</p> : null}
                {previewError && preview ? <p className={s.noteText}>{previewError}</p> : null}
                {preview?.previewDetailLevel === 'full' && !previewLoading ? (
                    <p className={s.noteText}>
                        This response used the full payout preview path. Redeploy the latest POSApi build for faster dashboard loads.
                    </p>
                ) : null}
                {previewLoading && !preview ? <p className={s.noteText}>Loading merchant balances…</p> : null}

                {preview ? (
                    <div className={`${s.detailsGrid} ${s.previewGrid}`}>
                        {canViewMerchantBalanceDetails ? (
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>Settled Balance</span>
                                <span className={s.detailValue}>{this.formatCurrency(preview.availableBalance)}</span>
                                <span className={s.detailDescription}>
                                    Settled funds in the merchant connected account.
                                </span>
                            </div>
                        ) : null}
                        {canViewMerchantBalanceDetails ? (
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>Stripe pending balance</span>
                                <span className={s.detailValue}>{this.formatCurrency(preview.pendingBalance)}</span>
                                <span className={s.detailDescription}>
                                    Incoming Stripe funds not yet available for payout.
                                </span>
                            </div>
                        ) : null}
                        {showFlatReserveCard ? (
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>Flat reserve</span>
                                <span className={s.detailValue}>{this.formatCurrency(preview.flatReserveAmount)}</span>
                                <span className={s.detailDescription}>
                                    Fixed dollar amount held when flat reserve is enabled.
                                </span>
                            </div>
                        ) : null}
                        {showPercentReserveCard ? (
                            <div className={s.detailItem}>
                                <span className={s.detailLabel}>Percentage reserve</span>
                                <span className={s.detailValue}>{this.formatCurrency(preview.percentReserveAmount)}</span>
                                <span className={s.detailDescription}>
                                    Share of available balance held when percentage reserve is enabled.
                                </span>
                            </div>
                        ) : null}
                        {showTotalTipsHeldCard ? (
                            <div className={`${s.detailItem} ${s.totalTipsHeldItem}`}>
                                <span className={s.detailLabel}>Total Unpaid Tips &amp; Gratuity</span>
                                <span className={s.detailValue}>{this.formatCurrency(totalUnpaidTipsGratuity)}</span>
                                <span className={s.detailDescription}>
                                    Total unpaid tips and gratuity owed across employees.
                                </span>
                            </div>
                        ) : null}
                        {showTipsGratuityCard ? (
                            <div className={`${s.detailItem} ${s.tipsGratuityItem}`}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.availableTips')}</span>
                                <div className={s.tipsGratuityValueRow}>
                                    <span className={s.tipsGratuityValue}>
                                        {this.formatCurrency(tipsAvailableToRelease)}
                                    </span>
                                    <div className={s.tipsGratuityPendingCol}>
                                        <span className={s.tipsGratuityPendingLabel}>Pending Tips &amp; Gratuity</span>
                                        <span className={s.tipsGratuityPendingValue}>
                                            {this.formatCurrency(tipsPending)}
                                        </span>
                                    </div>
                                </div>
                                <span className={s.detailDescription}>
                                    Available is tips and gratuity releasable with cleared funds.
                                    Pending is the unpaid remainder awaiting cleared funds.
                                </span>
                            </div>
                        ) : null}
                        {showTaxCard ? (
                            <div className={`${s.detailItem} ${s.taxItem}`}>
                                <span className={s.detailLabel}>Tax collected</span>
                                <div className={s.tipsGratuityValueRow}>
                                    <span className={s.tipsGratuityValue}>
                                        {this.formatCurrency(taxCollectedAmount)}
                                    </span>
                                    {preview.config?.reserveTaxEnabled || taxReserveAmount > 0 ? (
                                        <div className={s.tipsGratuityPendingCol}>
                                            <span className={s.tipsGratuityPendingLabel}>Tax reserve</span>
                                            <span className={s.tipsGratuityPendingValue}>
                                                {this.formatCurrency(taxReserveAmount)}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                                <span className={s.detailDescription}>
                                    Tax collected since {this.formatTaxCollectedSinceDate(preview)}.
                                    {preview.config?.reserveTaxEnabled
                                        ? ' Reserve Tax holds this amount on the connected account when funds allow.'
                                        : ''}
                                    {(preview.taxCollectedSource || preview.TaxCollectedSource)
                                        ? ` Source: ${preview.taxCollectedSource || preview.TaxCollectedSource}.`
                                        : ''}
                                    {' '}Separate from Tips &amp; Gratuity.
                                </span>
                                {this.props.canConfigureReserve && taxCollectedAmount > 0 ? (
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        type="button"
                                        className={s.taxCollectedResetBtn}
                                        disabled={this.state.taxCollectedResetBusy}
                                        onClick={this.openTaxCollectedResetConfirm}
                                    >
                                        Reset
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                        {canViewMerchantBalanceDetails ? (
                            <div className={`${s.detailItem} ${s.instantPayoutGridItem}`}>
                                <span className={s.detailLabel}>Instantly available</span>
                                <span className={s.instantPayoutValue}>{this.formatCurrency(instantEligible)}</span>
                                {preview.instantPayoutAvailable ? (
                                    <span className={s.detailDescription}>
                                        Includes pending balance (advance of unsettled funds). Instant payout fee: {instantFeePercent}%.
                                    </span>
                                ) : (
                                    <p className={s.instantPayoutNote}>Instant payout is not available for this account.</p>
                                )}
                            </div>
                        ) : null}
                        {canViewMerchantBalanceDetails ? (
                            <div className={`${s.detailItem} ${s.eligibleItem}`}>
                                <span className={s.detailLabel}>{this.props.t('payoutActions.availablePayout')}</span>
                                <span className={s.detailValue}>{this.formatCurrency(preview.autoPayoutEligible)}</span>
                                <span className={s.detailDescription}>
                                    Settled balance minus configured reserves, eligible for Standard Payout.
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {canViewMerchantBalanceDetails && preview && preview.reserveExceedsAvailableBalance ? (
                    <p className={s.warningText}>
                        Configured reserves exceed settled balance.
                        <br />
                        Configured reserves may show $0 until funds settle.
                    </p>
                ) : null}

                {this.renderStandardPayoutBlock()}
            </div>
        );
    }

    formatRecipientLabel(recipient) {
        if (!recipient) return '';
        const status = recipient.tipPayoutEnabled && recipient.stripeAccountStatus === 'active'
            ? 'Tips On'
            : (recipient.stripeAccountStatus || 'inactive');
        return `${recipient.username} (${recipient.roleName}) · ${status}`;
    }

    isRecipientConfiguredForTipPayout(recipient) {
        return !!recipient
            && recipient.tipPayoutEnabled
            && recipient.stripeAccountStatus === 'active'
            && !!recipient.stripeAccountId;
    }

    getPayRecipientRows() {
        const recipients = this.props.tipPayoutDirectRecipients || [];
        return recipients
            .filter((recipient) => this.isRecipientConfiguredForTipPayout(recipient))
            .map((recipient) => ({
                userId: recipient.userId,
                name: this.formatPayRecipientName(recipient),
                username: recipient.username || '',
                employeeId: recipient.employeeId || '',
                cleared: recipient.clearedTips ?? 0,
                tipsOwed: recipient.tipsOwed ?? 0,
                workPeriods: recipient.workPeriodBalances || [],
            }));
    }

    formatPayRecipientName(recipient) {
        if (!recipient) return '';
        if (recipient.employeeId) {
            return `${recipient.username} (${recipient.employeeId})`;
        }
        return recipient.username || '';
    }

    formatTipsOwedDisplay(tipsOwed) {
        if (tipsOwed === null || tipsOwed === undefined || Number.isNaN(Number(tipsOwed))) {
            return '—';
        }
        return this.formatCurrency(tipsOwed);
    }

    parseWorkPeriodDate(raw) {
        if (!raw) return null;
        const str = String(raw).trim();
        const datePart = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (datePart) {
            const year = parseInt(datePart[1], 10);
            const month = parseInt(datePart[2], 10) - 1;
            const day = parseInt(datePart[3], 10);
            const date = new Date(year, month, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }

        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    formatWorkPeriodDate(raw) {
        const date = this.parseWorkPeriodDate(raw);
        if (!date) return '—';
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayName = days[date.getDay()];
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);
        return `${dayName}. ${month}/${day}/${year}`;
    }

    formatPendingBreakdownEmployeeName(item) {
        if (!item) return '';
        if (item.employeeId) {
            return `${item.username} (${item.employeeId})`;
        }
        return item.username || '';
    }

    getPendingTipsBreakdownRows() {
        return this.props.tipPayoutSettings?.pendingTipsBreakdown || [];
    }

    openPendingTipsBreakdownModal = () => {
        this.setState({ pendingTipsBreakdownOpen: true }, () => {
            const breakdown = this.props.tipPayoutSettings?.pendingTipsBreakdown;
            if (!breakdown?.length && !this.props.tipPayoutPendingBreakdownLoading) {
                this.props.dispatch(loadMerchantTipPayoutPendingBreakdownRequest(this.getTipPayoutDateRange()));
            }
        });
    };

    closePendingTipsBreakdownModal = () => {
        this.setState({ pendingTipsBreakdownOpen: false });
    };

    getPaidToManagerBreakdownRows() {
        return this.props.tipPayoutSettings?.tipsPaidToManagerBreakdown || [];
    }

    openPaidToManagerBreakdownModal = () => {
        this.setState({ paidToManagerBreakdownOpen: true }, () => {
            const breakdown = this.props.tipPayoutSettings?.tipsPaidToManagerBreakdown;
            if (!breakdown?.length && !this.props.tipPayoutPaidToManagerBreakdownLoading) {
                this.props.dispatch(
                    loadMerchantTipPayoutPaidToManagerBreakdownRequest(this.getTipPayoutDateRange()),
                );
            }
        });
    };

    closePaidToManagerBreakdownModal = () => {
        this.setState({ paidToManagerBreakdownOpen: false });
    };

    getReleasableTipsBreakdownRows() {
        return (this.props.tipPayoutSettings?.releasableTipsBreakdown || [])
            .filter((row) => toNumber(row.amountReleasable) > 0);
    }

    getReleasableRowKey(row) {
        if (!row) return '';
        return `${row.userId}-${row.workPeriodId || 'none'}`;
    }

    canPayReleasableBreakdownRow(row) {
        return row
            && toNumber(row.amountReleasable) > 0
            && row.canPayDirect
            && this.props.canExecuteTipPayoutToRecipient
            && !this.isTipPayoutInProgress();
    }

    buildReleasablePartialPayNote(amountPaid, amountRemaining, message) {
        const paid = toNumber(amountPaid);
        const remaining = toNumber(amountRemaining);
        const msg = String(message || '').trim();
        if (paid <= 0 && !msg) {
            return '';
        }
        if (paid > 0) {
            const summary = `${this.formatCurrency(paid)} paid. ${this.formatCurrency(remaining)} remaining`;
            return msg ? `${summary} — ${msg}` : summary;
        }
        return msg;
    }

    getDirectPayAmountsFromResult(result, fallbackRemaining = 0) {
        if (!result) {
            return { amountPaid: 0, amountRemaining: toNumber(fallbackRemaining), message: '' };
        }

        const workPeriodResults = result.workPeriodResults ?? result.WorkPeriodResults;
        if (Array.isArray(workPeriodResults) && workPeriodResults.length > 0) {
            const item = workPeriodResults[0];
            const amountPaid = toNumber(item.amountPaid ?? item.AmountPaid);
            const amountRemaining = toNumber(
                item.amountRemaining ?? item.AmountRemaining
                ?? item.amountOwedAfter ?? item.AmountOwedAfter
                ?? Math.max(0, toNumber(fallbackRemaining) - amountPaid),
            );
            return {
                amountPaid,
                amountRemaining,
                message: result.message ?? result.Message ?? item.message ?? item.Message ?? '',
            };
        }

        const amountPaid = toNumber(result.amountPaid ?? result.AmountPaid);
        const amountRemaining = toNumber(
            result.amountRemaining ?? result.AmountRemaining
            ?? Math.max(0, toNumber(fallbackRemaining) - amountPaid),
        );
        return {
            amountPaid,
            amountRemaining,
            message: result.message ?? result.Message ?? '',
        };
    }

    openReleasableTipsBreakdownModal = () => {
        this.setState({
            releasableTipsBreakdownOpen: true,
            releasablePartialPayNote: '',
        }, () => {
            const breakdown = this.props.tipPayoutSettings?.releasableTipsBreakdown;
            if (!breakdown?.length && !this.props.tipPayoutReleasableBreakdownLoading) {
                this.props.dispatch(
                    loadMerchantTipPayoutReleasableBreakdownRequest(this.getTipPayoutDateRange()),
                );
            }
        });
    };

    closeReleasableTipsBreakdownModal = () => {
        if (this.state.payingReleasableRowKey) {
            return;
        }

        this.setState({
            releasableTipsBreakdownOpen: false,
            payingReleasableRowKey: null,
            releasablePartialPayNote: '',
        });
    };

    handlePayReleasableBreakdownRow = async (row) => {
        if (
            !row
            || toNumber(row.amountReleasable) <= 0
            || !row.canPayDirect
            || this.isTipPayoutInProgress()
        ) {
            return;
        }

        const rowKey = this.getReleasableRowKey(row);
        this.setState({ payingReleasableRowKey: rowKey, releasablePartialPayNote: '' });

        const displayName = this.formatPendingBreakdownEmployeeName(row);
        const outcome = await this.props.dispatch(
            payDirectTipsToEmployeeRequest(
                row.userId,
                displayName,
                this.getTipPayoutDateRange(),
                { workPeriodId: row.workPeriodId },
            ),
        );

        const amounts = this.getDirectPayAmountsFromResult(outcome?.result, row.amountReleasable);
        const showPartialNote = outcome?.partial
            || (amounts.amountPaid > 0 && amounts.amountRemaining > 0);

        if (showPartialNote) {
            this.setState({
                payingReleasableRowKey: null,
                releasablePartialPayNote: this.buildReleasablePartialPayNote(
                    amounts.amountPaid,
                    amounts.amountRemaining,
                    amounts.message,
                ),
            });
            return;
        }

        this.setState({
            payingReleasableRowKey: null,
            releasablePartialPayNote: '',
        });
    };

    renderReleasableTipsSummaryRow(releasable) {
        const canOpen = releasable > 0;
        const rowClassName = canOpen
            ? `${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryAvailableRow} ${s.tipPayoutSummaryRowClickable}`
            : `${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryAvailableRow}`;

        if (!canOpen) {
            return (
                <div className={rowClassName}>
                    <span>{this.props.t('payoutActions.availableTips')}</span>
                    <strong>{this.formatCurrency(releasable)}</strong>
                </div>
            );
        }

        return (
            <button
                type="button"
                className={rowClassName}
                onClick={this.openReleasableTipsBreakdownModal}
                aria-haspopup="dialog"
            >
                <span>{this.props.t('payoutActions.availableTips')}</span>
                <strong>{this.formatCurrency(releasable)}</strong>
            </button>
        );
    }

    renderPendingTipsSummaryRow(pending, { hideWhenZero = false } = {}) {
        if (hideWhenZero && pending <= 0) {
            return null;
        }

        const canOpen = pending > 0;
        const rowClassName = canOpen
            ? `${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryPendingRow} ${s.tipPayoutSummaryRowClickable}`
            : `${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryPendingRow}`;

        if (!canOpen) {
            return (
                <div className={rowClassName}>
                    <span>Pending Tips &amp; Gratuity</span>
                    <strong>{this.formatCurrency(pending)}</strong>
                </div>
            );
        }

        return (
            <button
                type="button"
                className={rowClassName}
                onClick={this.openPendingTipsBreakdownModal}
                aria-haspopup="dialog"
            >
                <span>Pending Tips &amp; Gratuity</span>
                <strong>{this.formatCurrency(pending)}</strong>
            </button>
        );
    }

    renderPaidToManagerSummaryRow(paidToManager) {
        if (paidToManager <= 0) {
            return null;
        }

        return (
            <button
                type="button"
                className={`${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryPaidToManagerRow} ${s.tipPayoutSummaryRowClickable}`}
                onClick={this.openPaidToManagerBreakdownModal}
                aria-haspopup="dialog"
            >
                <span>Tips and gratuity paid to manager</span>
                <strong>{this.formatCurrency(paidToManager)}</strong>
            </button>
        );
    }

    renderTotalUnpaidTipsSummaryRow(totalUnpaid) {
        return (
            <div className={`${s.tipPayoutSummaryRow} ${s.tipPayoutSummaryTotalRow}`}>
                <span>Total Unpaid Tips &amp; Gratuity</span>
                <strong>{this.formatCurrency(totalUnpaid)}</strong>
            </div>
        );
    }

    isTipPayoutInProgress() {
        const {
            tipPayoutPaying,
            tipPayoutDirectPaying,
            tipPayoutMarkAllLoading,
        } = this.props;

        return !!(
            tipPayoutPaying
            || tipPayoutDirectPaying
            || tipPayoutMarkAllLoading
            || this.state.payingReleasableRowKey
        );
    }

    canPayRecipientRow(row) {
        return row
            && toNumber(row.cleared) > 0
            && !this.isTipPayoutInProgress();
    }

    getEmployeeWorkPeriodModalRows(workPeriods = []) {
        return (workPeriods || [])
            .filter((workPeriod) => toNumber(workPeriod.amountOwed) > 0 || toNumber(workPeriod.amountCleared) > 0)
            .sort((a, b) => {
                const aDate = this.parseWorkPeriodDate(a.workPeriodDate);
                const bDate = this.parseWorkPeriodDate(b.workPeriodDate);
                if (!aDate || !bDate) return 0;
                return bDate - aDate;
            });
    }

    openEmployeeWorkPeriodModal = (row) => {
        if (!row) {
            return;
        }
        this.setState({ employeeWorkPeriodModalRow: row });
    };

    closeEmployeeWorkPeriodModal = () => {
        this.setState({ employeeWorkPeriodModalRow: null });
    };

    buildEmployeeSnapshotFromRow(row) {
        if (!row) {
            return null;
        }

        return {
            UserId: row.userId,
            Username: row.username || row.name,
            EmployeeId: row.employeeId || '',
        };
    }

    buildWorkperiodSnapshotFromBalance(workPeriod) {
        if (!workPeriod) {
            return null;
        }

        const workPeriodId = workPeriod.workPeriodId;
        const owed = toNumber(workPeriod.amountOwed);

        return {
            WorkPeriodId: workPeriodId,
            workPeriodId: workPeriodId,
            WorkperiodId: workPeriodId,
            workperiodId: workPeriodId,
            WorkperiodDate: workPeriod.workPeriodDate,
            workperiodDate: workPeriod.workPeriodDate,
            Tips: owed,
            tips: owed,
            Gratuity: 0,
            gratuity: 0,
        };
    }

    handleEmployeeWorkPeriodDrillDown = (workPeriod, employeeRow) => {
        const workPeriodId = workPeriod?.workPeriodId;
        const userId = employeeRow?.userId;
        if (!workPeriodId || !userId) {
            return;
        }

        const employee = this.buildEmployeeSnapshotFromRow(employeeRow);
        const workperiod = this.buildWorkperiodSnapshotFromBalance(workPeriod);

        this.closeEmployeeWorkPeriodModal();
        this.props.history.push({
            pathname: `/admin/users/sales/${userId}/workperiod/${workPeriodId}`,
            state: {
                workperiod,
                employee,
                returnPath: PAYOUT_SETTINGS_PATH,
            },
        });
    };

    openPayRecipientConfirmModal = (row) => {
        if (this.isTipPayoutInProgress() || !row || toNumber(row.cleared) <= 0) {
            return;
        }
        this.setState({ payRecipientConfirm: row });
    };

    closePayRecipientConfirmModal = () => {
        if (this.props.tipPayoutDirectPaying) {
            return;
        }
        this.setState({ payRecipientConfirm: null });
    };

    confirmPayRecipient = async () => {
        const { payRecipientConfirm } = this.state;
        if (!payRecipientConfirm || this.props.tipPayoutDirectPaying) {
            return;
        }

        const outcome = await this.props.dispatch(
            payDirectTipsToEmployeeRequest(
                payRecipientConfirm.userId,
                payRecipientConfirm.name,
                this.getTipPayoutDateRange(),
            ),
        );
        if (outcome?.success || outcome?.partial) {
            this.closePayRecipientConfirmModal();
        }
    };

    handleDesignatedRecipientChange = (event) => {
        this.setState({ designatedRecipientDraft: event.target.value });
    };

    handleSaveDesignatedRecipient = (event) => {
        event.preventDefault();
        const { designatedRecipientDraft } = this.state;
        const userId = designatedRecipientDraft ? Number(designatedRecipientDraft) : null;
        this.props.dispatch(saveMerchantTipPayoutSettingsRequest(userId));
    };

    openPayTipsConfirmModal = () => {
        if (this.isTipPayoutInProgress() || this.getPayTipsDisabledReason()) {
            return;
        }
        this.setState({ payTipsConfirmOpen: true });
    };

    closePayTipsConfirmModal = () => {
        if (this.props.tipPayoutPaying) {
            return;
        }
        this.setState({ payTipsConfirmOpen: false });
    };

    confirmPayTipsToRecipient = async () => {
        if (this.props.tipPayoutPaying) {
            return;
        }

        const { preview, tipPayoutSettings } = this.props;
        const designatedRecipient = tipPayoutSettings?.designatedRecipient;
        const releaseContext = {
            tipPayoutSettings,
            directRecipients: this.props.tipPayoutDirectRecipients || [],
            releaseAmount: this.getTipsAvailableToReleaseAmount(preview, tipPayoutSettings, { forManagerSummary: true }),
            recipientName: designatedRecipient?.username || '',
            payoutPath: PAYOUT_PATH_MANAGER,
        };

        const outcome = await this.props.dispatch(
            payReservedTipsToRecipientRequest(releaseContext, this.getTipPayoutDateRange()),
        );
        if (outcome?.success || outcome?.partial) {
            this.setState({ payTipsConfirmOpen: false });
        }
    };

    openMarkAllPaidConfirmModal = () => {
        if (
            !isRebelityPlatformAdmin(this.props)
            || this.isTipPayoutInProgress()
            || this.getMarkAllPaidDisabledReason()
        ) {
            return;
        }
        this.setState({ markAllPaidConfirmOpen: true });
    };

    closeMarkAllPaidConfirmModal = () => {
        if (this.props.tipPayoutMarkAllLoading) {
            return;
        }
        this.setState({ markAllPaidConfirmOpen: false });
    };

    handleMarkAllPaidReasonChange = (event) => {
        this.setState({ markAllPaidReason: event.target.value });
    };

    confirmMarkAllOutstandingTipsPaid = async () => {
        if (!isRebelityPlatformAdmin(this.props) || this.props.tipPayoutMarkAllLoading) {
            return;
        }

        const reason = String(this.state.markAllPaidReason || '').trim();
        if (!reason) {
            window.alert('A reason is required before marking outstanding tips as paid.');
            return;
        }

        const outcome = await this.props.dispatch(
            markAllOutstandingTipsPaidRequest(reason, this.getTipPayoutDateRange()),
        );
        if (outcome?.success || outcome?.partial) {
            this.setState({ markAllPaidConfirmOpen: false });
        }
    };

    getMarkAllPaidDisabledReason() {
        const { tipPayoutSettings, preview } = this.props;
        const { totalUnpaid } = this.getManagerSummaryAmounts(tipPayoutSettings, preview);
        if (totalUnpaid <= 0) {
            return 'No outstanding tip obligations were found for the selected work period.';
        }
        return '';
    }

    getPayTipsDisabledReason() {
        const { tipPayoutSettings, preview } = this.props;
        const recipient = tipPayoutSettings?.designatedRecipient;
        const availableToRelease = this.getTipsAvailableToReleaseAmount(
            preview,
            tipPayoutSettings,
            { forManagerSummary: true },
        );
        const routingMode = tipPayoutSettings?.tipPayoutRoutingMode || preview?.tipPayoutRoutingMode;
        const isPlatform = this.isPlatformTipRouting(routingMode);

        if (!recipient) {
            return 'A designated Manager or Admin recipient must be configured by Rebelity before tips can be paid.';
        }
        if (availableToRelease <= 0) {
            const pending = this.getManagerSummaryAmounts(tipPayoutSettings, preview).pending;
            if (pending > 0) {
                return isPlatform
                    ? 'Tips are still held on the platform awaiting cleared funds before they can be paid to the manager.'
                    : 'Tips are still held while awaiting cleared Stripe funds before they can be paid out.';
            }
            return 'No tips or gratuity are available to release.';
        }
        return '';
    }

    renderTipPayoutDateFilter() {
        const {
            tipPayoutDatePreset,
            tipPayoutCustomStartDate,
            tipPayoutCustomEndDate,
        } = this.state;
        const isCustom = tipPayoutDatePreset === TIP_PAYOUT_DATE_PRESET_CUSTOM;
        const customRangeInvalid = isCustom
            && tipPayoutCustomStartDate
            && tipPayoutCustomEndDate
            && tipPayoutCustomStartDate > tipPayoutCustomEndDate;

        return (
            <div className={s.tipPayoutDateFilter}>
                <div className={s.tipPayoutDateFilterRow}>
                    <span className={s.tipPayoutDateFilterLabel}>Work period</span>
                    <div className={s.tipPayoutDatePresets} role="group" aria-label="Work period date range">
                        {TIP_PAYOUT_DATE_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className={`${s.tipPayoutDatePreset}${
                                    tipPayoutDatePreset === preset.id ? ` ${s.tipPayoutDatePresetActive}` : ''
                                }`}
                                aria-pressed={tipPayoutDatePreset === preset.id}
                                onClick={() => this.handleTipPayoutDatePresetChange(preset.id)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>
                {isCustom ? (
                    <div className={s.tipPayoutDateCustom}>
                        <Input
                            type="date"
                            value={tipPayoutCustomStartDate}
                            onChange={this.handleTipPayoutCustomStartDateChange}
                            aria-label="Work period start date"
                        />
                        <span className={s.tipPayoutDateCustomSeparator}>to</span>
                        <Input
                            type="date"
                            value={tipPayoutCustomEndDate}
                            onChange={this.handleTipPayoutCustomEndDateChange}
                            aria-label="Work period end date"
                        />
                        <Button
                            color="secondary"
                            type="button"
                            outline
                            className={s.tipPayoutDateApplyBtn}
                            disabled={customRangeInvalid}
                            onClick={this.applyTipPayoutCustomDateRange}
                        >
                            Apply
                        </Button>
                    </div>
                ) : null}
                {customRangeInvalid ? (
                    <p className={s.errorText}>Start date must be on or before end date.</p>
                ) : null}
            </div>
        );
    }

    renderTipPayoutModeToggle() {
        const { tipPayoutViewMode } = this.state;
        const canSwitch = this.canSwitchTipPayoutViewMode();
        const toggleClassName = [
            s.tipPayoutModeToggle,
            canSwitch ? '' : s.tipPayoutModeToggleReadOnly,
        ].filter(Boolean).join(' ');

        return (
            <div
                className={toggleClassName}
                role="tablist"
                aria-label="Tip payout mode"
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={tipPayoutViewMode === TIP_PAYOUT_MODE_MANAGER}
                    className={`${s.tipPayoutModePill}${
                        tipPayoutViewMode === TIP_PAYOUT_MODE_MANAGER ? ` ${s.tipPayoutModePillActive}` : ''
                    }`}
                    disabled={!canSwitch}
                    onClick={() => this.handleTipPayoutViewModeChange(TIP_PAYOUT_MODE_MANAGER)}
                >
                    Manager Tip Payout
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tipPayoutViewMode === TIP_PAYOUT_MODE_EMPLOYEE}
                    className={`${s.tipPayoutModePill}${
                        tipPayoutViewMode === TIP_PAYOUT_MODE_EMPLOYEE ? ` ${s.tipPayoutModePillActive}` : ''
                    }`}
                    disabled={!canSwitch}
                    onClick={() => this.handleTipPayoutViewModeChange(TIP_PAYOUT_MODE_EMPLOYEE)}
                >
                    Pay Tips Directly
                </button>
            </div>
        );
    }

    renderEmployeeTipPayoutContent() {
        const {
            tipPayoutDirectRecipientsLoading,
            tipPayoutDirectRecipientsError,
            tipPayoutDirectPaying,
            tipPayoutDirectPayingUserId,
        } = this.props;
        const rows = this.getPayRecipientRows();

        return (
            <>
                <p className={s.noteText}>
                    Employees with an active Stripe Connect account configured for tip payouts.
                    Click an employee name to view cleared and owed amounts by work period.
                </p>

                {tipPayoutDirectRecipientsError ? <p className={s.errorText}>{tipPayoutDirectRecipientsError}</p> : null}

                {tipPayoutDirectRecipientsLoading && rows.length === 0 ? (
                    <p className={s.noteText}>Loading eligible recipients…</p>
                ) : null}

                {!tipPayoutDirectRecipientsLoading && rows.length === 0 ? (
                    <p className={s.noteText}>
                        No employees with an active Stripe Connect account configured for tip payouts were found.
                        Connect Stripe and enable tip payouts on the{' '}
                        <a className={s.settingsLink} href="/admin/users">Users</a> page first.
                    </p>
                ) : null}

                {rows.length > 0 ? (
                    <div className={s.payRecipientsTableWrap}>
                        <table className={s.payRecipientsTable}>
                            <thead>
                                <tr>
                                    <th scope="col">Employee</th>
                                    <th scope="col">Cleared</th>
                                    <th scope="col">Owed</th>
                                    <th scope="col" className={s.payRecipientsActionCol}>Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const modalWorkPeriods = this.getEmployeeWorkPeriodModalRows(row.workPeriods);
                                    const canOpenWorkPeriodModal = modalWorkPeriods.length > 0;

                                    const isPayingRow = tipPayoutDirectPaying
                                        && Number(tipPayoutDirectPayingUserId) === Number(row.userId);
                                    const canPayRow = toNumber(row.cleared) > 0
                                        && this.props.canExecuteTipPayoutToRecipient;

                                    return (
                                        <tr key={row.userId} className={s.payRecipientsParentRow}>
                                            <td className={s.payRecipientsNameCell}>
                                                {canOpenWorkPeriodModal ? (
                                                    <button
                                                        type="button"
                                                        className={s.payRecipientsNameBtn}
                                                        onClick={() => this.openEmployeeWorkPeriodModal(row)}
                                                        aria-haspopup="dialog"
                                                    >
                                                        {row.name}
                                                    </button>
                                                ) : (
                                                    row.name
                                                )}
                                            </td>
                                            <td className={s.payRecipientsAmountCell}>
                                                {this.formatTipsOwedDisplay(row.cleared)}
                                            </td>
                                            <td className={s.payRecipientsAmountCell}>
                                                {this.formatTipsOwedDisplay(row.tipsOwed)}
                                            </td>
                                            <td className={s.payRecipientsActionCol}>
                                                {canPayRow ? (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        type="button"
                                                        className={s.payRecipientsPayBtn}
                                                        disabled={this.isTipPayoutInProgress()}
                                                        onClick={() => this.openPayRecipientConfirmModal(row)}
                                                    >
                                                        {isPayingRow ? 'Paying…' : 'Pay'}
                                                    </Button>
                                                ) : (
                                                    <span className={s.noteText}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </>
        );
    }

    renderManagerTipPayoutContent() {
        const {
            preview,
            previewLoading,
            tipPayoutSettings,
            tipPayoutSettingsLoading,
            tipPayoutSettingsError,
            tipPayoutSettingsSaving,
            tipPayoutPaying,
            tipPayoutPayError,
            tipPayoutMarkAllLoading,
            tipPayoutMarkAllError,
            tipPayoutDesignatedRecipientsLoading,
            tipPayoutDesignatedRecipientsError,
            canConfigureTipPayoutRecipient,
            canExecuteTipPayoutToRecipient,
        } = this.props;

        const waitingForPreview = this.isManagerSummaryLoading()
            && !this.props.preview
            && this.props.previewLoading;
        const summaryLoading = this.isManagerSummaryLoading();

        const recipientsLoading = tipPayoutDesignatedRecipientsLoading
            || (tipPayoutSettingsLoading && !(tipPayoutSettings?.eligibleRecipients || []).length);
        const routingMode = tipPayoutSettings?.tipPayoutRoutingMode || preview?.tipPayoutRoutingMode;
        const routing = this.getTipPayoutRoutingInfo(routingMode);
        const designatedRecipient = tipPayoutSettings?.designatedRecipient;
        const { pending, totalUnpaid, paidToManager: paidToManagerFromSummary } = this.getManagerSummaryAmounts(
            tipPayoutSettings,
            preview,
        );
        const tipsAvailableToRelease = this.getTipsAvailableToReleaseAmount(
            preview,
            tipPayoutSettings,
            { forManagerSummary: true },
        );
        const isPlatformRouting = this.isPlatformTipRouting(routingMode);
        const paidToManager = isPlatformRouting ? toNumber(paidToManagerFromSummary) : 0;
        const payDisabledReason = this.getPayTipsDisabledReason();
        const canPay = canExecuteTipPayoutToRecipient && !payDisabledReason && !this.isTipPayoutInProgress();
        const markAllDisabledReason = this.getMarkAllPaidDisabledReason();
        const canMarkAllPaid = isRebelityPlatformAdmin(this.props)
            && canExecuteTipPayoutToRecipient
            && !markAllDisabledReason;
        const payButtonLabel = designatedRecipient
            ? `Release Available Tips To Manager (${designatedRecipient.username})`
            : 'Release Available Tips To Manager';

        return (
            <>
                <div className={s.tipPayoutManagerLayout}>
                    <div className={s.tipPayoutManagerConfigCol}>
                        <div className={s.tipPayoutSettingItem}>
                            <span className={s.tipPayoutSettingLabel}>{this.props.t('payoutActions.tipRoutingMode')}</span>
                            <span className={s.tipPayoutSettingValue}>
                                {previewLoading && !preview && waitingForPreview && !tipPayoutSettings
                                    ? 'Loading…'
                                    : routing.label}
                            </span>
                            <p className={`${s.noteText} ${s.tipPayoutRoutingDescription}`}>
                                {routing.description}
                            </p>
                        </div>

                        {tipPayoutSettingsError ? (
                            <p className={s.errorText}>{tipPayoutSettingsError}</p>
                        ) : null}
                        {tipPayoutDesignatedRecipientsError ? (
                            <p className={s.errorText}>{tipPayoutDesignatedRecipientsError}</p>
                        ) : null}

                        <div className={s.tipPayoutSettingItem}>
                            <span className={s.tipPayoutSettingLabel}>{this.props.t('payoutActions.designatedRecipient')}</span>
                            {canConfigureTipPayoutRecipient ? (
                                <>
                                <Form onSubmit={this.handleSaveDesignatedRecipient}>
                                    <div className={s.tipPayoutDesignationRow}>
                                        <Input
                                            id="designatedTipRecipient"
                                            type="select"
                                            className={s.tipPayoutDesignationSelect}
                                            value={this.state.designatedRecipientDraft}
                                            onChange={this.handleDesignatedRecipientChange}
                                            disabled={
                                                tipPayoutSettingsSaving
                                                || recipientsLoading
                                            }
                                        >
                                            <option value="">None — no manager fallback / release</option>
                                            {(tipPayoutSettings?.eligibleRecipients || []).map((recipient) => (
                                                <option key={recipient.userId} value={String(recipient.userId)}>
                                                    {this.formatRecipientLabel(recipient)}
                                                </option>
                                            ))}
                                        </Input>
                                        <div className={s.tipPayoutDesignationActionCol}>
                                            <Button
                                                color="primary"
                                                type="submit"
                                                size="sm"
                                                className={s.tipPayoutSaveBtn}
                                                disabled={
                                                    tipPayoutSettingsSaving
                                                    || recipientsLoading
                                                }
                                            >
                                                {tipPayoutSettingsSaving ? 'Saving…' : 'Save'}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                                <p className={s.noteText}>
                                    Tips On employees receive tips at sale time. The designated recipient
                                    receives sale-time tips only when that employee’s Stripe is not
                                    correctly configured, and is also used for Release Available Tips To Manager.
                                </p>
                                {!recipientsLoading
                                    && !(tipPayoutSettings?.eligibleRecipients || []).length ? (
                                    <p className={s.noteText}>
                                        No Manager or Admin employees with an active Stripe Connect account were found.
                                        Connect Stripe and enable tip payouts on the Users page first.
                                    </p>
                                ) : null}
                                </>
                            ) : (
                                <span className={s.tipPayoutSettingValue}>
                                    {recipientsLoading && !designatedRecipient
                                        ? 'Loading…'
                                        : designatedRecipient
                                            ? this.formatRecipientLabel(designatedRecipient)
                                            : 'No designated recipient — Tips On employees paid at sale time; no manager fallback.'}
                                </span>
                            )}
                        </div>

                        {!canConfigureTipPayoutRecipient && !designatedRecipient ? (
                            <p className={s.noteText}>
                                Contact Rebelity to configure a designated Manager or Admin tip recipient
                                for releasing unpaid tips.
                            </p>
                        ) : null}
                    </div>

                    <div className={s.tipPayoutManagerSummaryCol}>
                        <div className={s.tipPayoutSummary}>
                            {summaryLoading ? (
                                <p className={s.noteText}>Updating release amounts…</p>
                            ) : (
                                <>
                                    {this.shouldShowMerchantSplitInTipPayoutSummary(tipPayoutSettings, preview)
                                        ? this.renderReleasableTipsSummaryRow(tipsAvailableToRelease)
                                        : null}
                                    {this.shouldShowMerchantSplitInTipPayoutSummary(tipPayoutSettings, preview)
                                        ? this.renderPendingTipsSummaryRow(pending, {
                                            hideWhenZero: isPlatformRouting,
                                        })
                                        : null}
                                    {isPlatformRouting
                                        ? this.renderPaidToManagerSummaryRow(paidToManager)
                                        : null}
                                    {this.shouldShowManagerTotalUnpaidRow(totalUnpaid)
                                        ? this.renderTotalUnpaidTipsSummaryRow(totalUnpaid)
                                        : null}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {canExecuteTipPayoutToRecipient ? (
                    <div className={s.tipPayoutManagerPayRow}>
                        <Button
                            color="primary"
                            type="button"
                            className={s.tipPayoutPayBtn}
                            disabled={!canPay || summaryLoading}
                            onClick={this.openPayTipsConfirmModal}
                        >
                            {tipPayoutPaying ? 'Processing…' : payButtonLabel}
                        </Button>
                        <p className={s.noteText}>
                            Releases remaining unpaid tips. Tips already paid at sale time to Tips On
                            employees (or via manager fallback) are not included.
                        </p>
                        {!canPay && payDisabledReason ? (
                            <p className={s.noteText}>{payDisabledReason}</p>
                        ) : null}
                        {tipPayoutPayError ? (
                            <p className={s.errorText}>{tipPayoutPayError}</p>
                        ) : null}
                        {isRebelityPlatformAdmin(this.props) ? (
                            <>
                                <Button
                                    color="secondary"
                                    type="button"
                                    className={s.tipPayoutMarkAllBtn}
                                    disabled={!canMarkAllPaid || tipPayoutMarkAllLoading || this.isTipPayoutInProgress() || summaryLoading}
                                    onClick={this.openMarkAllPaidConfirmModal}
                                >
                                    {tipPayoutMarkAllLoading ? 'Marking…' : 'Mark All Outstanding Tips As Paid'}
                                </Button>
                                {!canMarkAllPaid && markAllDisabledReason ? (
                                    <p className={s.noteText}>{markAllDisabledReason}</p>
                                ) : null}
                                {tipPayoutMarkAllError ? (
                                    <p className={s.errorText}>{tipPayoutMarkAllError}</p>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                ) : null}
            </>
        );
    }

    renderDisabledTipRoutingPanel() {
        const routing = this.getTipPayoutRoutingInfo(this.getTipPayoutRoutingMode());
        return (
            <div className={`${s.reserveControlsCard} ${s.tipPayoutPanelCard}`}>
                <div className={s.reserveControlsBody}>
                    <div className={s.tipPayoutSettingItem}>
                        <span className={s.tipPayoutSettingLabel}>{this.props.t('payoutActions.tipRoutingMode')}</span>
                        <span className={s.tipPayoutSettingValue}>{routing.label}</span>
                        <p className={`${s.noteText} ${s.tipPayoutRoutingDescription}`}>
                            {routing.description}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    renderTipPayoutPanel() {
        if (this.isTipPayoutDisabled()) {
            return this.renderDisabledTipRoutingPanel();
        }

        const { tipPayoutViewMode } = this.state;
        const { canViewTipSummaryReadOnly } = this.props;
        const isManagerMode = tipPayoutViewMode === TIP_PAYOUT_MODE_MANAGER;

        return (
            <div className={`${s.reserveControlsCard} ${s.tipPayoutPanelCard}`}>
                {this.renderTipPayoutDateFilter()}
                {!canViewTipSummaryReadOnly ? this.renderTipPayoutModeToggle() : null}
                <div className={s.reserveControlsBody}>
                    {isManagerMode
                        ? this.renderManagerTipPayoutContent()
                        : this.renderEmployeeTipPayoutContent()}
                </div>
                {this.renderRecentSuccessfulTipPayouts()}
            </div>
        );
    }

    formatManagerReleaseStatusLabel = (status) => {
        const normalized = String(status || '').trim().toLowerCase();
        if (normalized === 'completed') return 'Completed';
        if (normalized === 'partial') return 'Partial';
        if (normalized === 'failed') return 'Failed';
        return 'Unknown';
    };

    getManagerReleaseStatusClassName = (status) => {
        const normalized = String(status || '').trim().toLowerCase();
        if (normalized === 'completed') return s.recentTipPayoutStatusCompleted;
        if (normalized === 'partial') return s.recentTipPayoutStatusPartial;
        if (normalized === 'failed') return s.recentTipPayoutStatusFailed;
        return s.recentTipPayoutStatusUnknown;
    };

    renderRecentSuccessfulTipPayouts() {
        const {
            recentSuccessfulTipPayouts,
            recentSuccessfulTipPayoutsLoading,
            recentSuccessfulTipPayoutsError,
            canViewTipPayoutSettings,
            canViewTipSummaryReadOnly,
        } = this.props;

        if (!canViewTipPayoutSettings && !canViewTipSummaryReadOnly) {
            return null;
        }

        const rows = recentSuccessfulTipPayouts || [];

        return (
            <div className={s.recentTipPayoutsSection}>
                <div className={s.tipPayoutSummary}>
                    <div className={s.sectionHeader}>
                        <span className={s.sectionTitle}>Recent successful tip payouts</span>
                        <button
                            type="button"
                            className={s.refreshBtn}
                            onClick={() => this.props.dispatch(loadRecentSuccessfulTipPayoutsRequest())}
                            disabled={recentSuccessfulTipPayoutsLoading}
                        >
                            {recentSuccessfulTipPayoutsLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                    <p className={s.noteText}>
                        Last 10 manager consolidated tip releases for this merchant, including completed, partial, and failed attempts.
                    </p>
                    {recentSuccessfulTipPayoutsError ? (
                        <p className={s.errorText}>{recentSuccessfulTipPayoutsError}</p>
                    ) : null}
                    {recentSuccessfulTipPayoutsLoading && rows.length === 0 ? (
                        <p className={s.noteText}>Loading recent payouts…</p>
                    ) : rows.length > 0 ? (
                        <div className={s.payRecipientsTableWrap}>
                            <table className={s.payRecipientsTable}>
                                <thead>
                                    <tr>
                                        <th scope="col">Date</th>
                                        <th scope="col">Recipient</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Transferred</th>
                                        <th scope="col">Remaining</th>
                                        <th scope="col">Stripe transfer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.releaseId || `${row.recipientUserId}-${row.attemptedAt}`}>
                                            <td className={s.recentTipPayoutsMetaCell}>
                                                {this.formatPayoutDateTime(row.attemptedAt)}
                                            </td>
                                            <td className={s.payRecipientsNameCell}>
                                                {row.recipientUsername || (row.recipientUserId ? `User ${row.recipientUserId}` : '—')}
                                            </td>
                                            <td className={s.recentTipPayoutsMetaCell}>
                                                <span
                                                    className={`${s.recentTipPayoutStatusPill} ${this.getManagerReleaseStatusClassName(row.status)}`}
                                                    title={row.message || ''}
                                                >
                                                    {this.formatManagerReleaseStatusLabel(row.status)}
                                                </span>
                                            </td>
                                            <td className={s.recentTipPayoutsAmountCell}>
                                                {this.formatCurrency(row.transferAmount)}
                                            </td>
                                            <td className={s.recentTipPayoutsMetaCell}>
                                                {this.formatCurrency(row.remainingAmount)}
                                            </td>
                                            <td
                                                className={s.recentTipPayoutsMetaCell}
                                                title={row.stripeTransferId || row.message || ''}
                                            >
                                                {this.formatStripeTransferId(row.stripeTransferId)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : !recentSuccessfulTipPayoutsLoading ? (
                        <p className={s.noteText}>No manager tip releases recorded yet.</p>
                    ) : null}
                </div>
            </div>
        );
    }

    renderReadOnlyTipsDashboard() {
        if (this.isTipPayoutDisabled()) {
            return (
                <div className={s.page}>
                    <Widget title="Tips" className={s.payoutWidget}>
                        {this.renderDisabledTipRoutingPanel()}
                    </Widget>
                </div>
            );
        }

        const {
            tipPayoutSettings,
            tipPayoutSettingsLoading,
            tipPayoutSettingsError,
            merchant,
        } = this.props;
        const storeName = merchant?.StoreName || 'Current merchant';
        const summaryLoading = this.isManagerSummaryLoading();
        const { pending, totalUnpaid } = this.getManagerSummaryAmounts(tipPayoutSettings, null);
        const tipsAvailableToRelease = this.getTipsAvailableToReleaseAmount(
            null,
            tipPayoutSettings,
            { forManagerSummary: true },
        );
        const showMerchantTipsGratuityLiquidity = this.shouldShowMerchantTipsGratuityLiquidity();

        return (
            <div className={s.page}>
                <Widget title="Tips" className={s.payoutWidget}>
                    <div className={s.previewPanel}>
                        <div className={s.sectionHeader}>
                            <span className={s.sectionTitle}>{this.props.t('payoutActions.tipsOverview')}</span>
                            <button
                                type="button"
                                className={s.refreshBtn}
                                onClick={() => {
                                    this.props.dispatch(getTipFundingSummaryRequest());
                                    this.props.dispatch(
                                        loadMerchantTipPayoutManagerSummaryRequest(this.getTipPayoutDateRange()),
                                    );
                                }}
                                disabled={tipPayoutSettingsLoading}
                            >
                                {tipPayoutSettingsLoading ? 'Refreshing…' : 'Refresh'}
                            </button>
                        </div>

                        <p className={s.storeName}>{storeName}</p>
                        {tipPayoutSettingsError ? <p className={s.errorText}>{tipPayoutSettingsError}</p> : null}

                        <div className={`${s.detailsGrid} ${s.previewGrid}`}>
                            <div className={`${s.detailItem} ${s.totalTipsHeldItem}`}>
                                <span className={s.detailLabel}>Total Unpaid Tips &amp; Gratuity</span>
                                <span className={s.detailValue}>
                                    {summaryLoading ? '…' : this.formatCurrency(totalUnpaid)}
                                </span>
                                <span className={s.detailDescription}>
                                    Total unpaid tips and gratuity owed across employees.
                                </span>
                            </div>
                            {showMerchantTipsGratuityLiquidity ? (
                                <div className={`${s.detailItem} ${s.tipsGratuityItem}`}>
                                    <span className={s.detailLabel}>{this.props.t('payoutActions.availableTips')}</span>
                                    <div className={s.tipsGratuityValueRow}>
                                        <span className={s.tipsGratuityValue}>
                                            {summaryLoading ? '…' : this.formatCurrency(tipsAvailableToRelease)}
                                        </span>
                                        <div className={s.tipsGratuityPendingCol}>
                                            <span className={s.tipsGratuityPendingLabel}>Pending Tips &amp; Gratuity</span>
                                            <span className={s.tipsGratuityPendingValue}>
                                                {summaryLoading ? '…' : this.formatCurrency(pending)}
                                            </span>
                                        </div>
                                    </div>
                                    <span className={s.detailDescription}>
                                        Available is tips and gratuity releasable with cleared funds.
                                        Pending is the unpaid remainder awaiting cleared funds.
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={s.readOnlyTipPayoutSection}>
                        {this.renderTipPayoutPanel()}
                    </div>
                </Widget>

                {this.renderPendingTipsBreakdownModal()}
                {this.renderPaidToManagerBreakdownModal()}
                {this.renderReleasableTipsBreakdownModal()}
            </div>
        );
    }

    renderEmployeeWorkPeriodModal() {
        const { employeeWorkPeriodModalRow } = this.state;
        if (!employeeWorkPeriodModalRow) {
            return null;
        }

        const rows = this.getEmployeeWorkPeriodModalRows(employeeWorkPeriodModalRow.workPeriods);

        return (
            <Modal
                isOpen
                toggle={this.closeEmployeeWorkPeriodModal}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={this.closeEmployeeWorkPeriodModal}
                    className={s.confirmModalHeader}
                >
                    {employeeWorkPeriodModalRow.name}
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Cleared and owed tips by work period. Click a row to open shift details.
                    </p>
                    {rows.length > 0 ? (
                        <div className={s.employeeWorkPeriodModalTableWrap}>
                            <table className={s.employeeWorkPeriodModalTable}>
                                <thead>
                                    <tr>
                                        <th scope="col">Work period</th>
                                        <th scope="col">Cleared</th>
                                        <th scope="col">Owed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((workPeriod) => {
                                        const canDrillDown = !!workPeriod.workPeriodId;
                                        const rowKey = `${employeeWorkPeriodModalRow.userId}-${workPeriod.workPeriodId || 'none'}`;

                                        if (!canDrillDown) {
                                            return (
                                                <tr key={rowKey}>
                                                    <td>{this.formatWorkPeriodDate(workPeriod.workPeriodDate)}</td>
                                                    <td>{this.formatTipsOwedDisplay(workPeriod.amountCleared)}</td>
                                                    <td>{this.formatTipsOwedDisplay(workPeriod.amountOwed)}</td>
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr key={rowKey}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className={s.employeeWorkPeriodModalRowBtn}
                                                        onClick={() => this.handleEmployeeWorkPeriodDrillDown(
                                                            workPeriod,
                                                            employeeWorkPeriodModalRow,
                                                        )}
                                                    >
                                                        {this.formatWorkPeriodDate(workPeriod.workPeriodDate)}
                                                    </button>
                                                </td>
                                                <td>{this.formatTipsOwedDisplay(workPeriod.amountCleared)}</td>
                                                <td>{this.formatTipsOwedDisplay(workPeriod.amountOwed)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className={s.noteText}>No work periods with tip obligations in the selected range.</p>
                    )}
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.closeEmployeeWorkPeriodModal}
                        >
                            Close
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderMarkAllPaidConfirmModal() {
        if (!this.state.markAllPaidConfirmOpen) {
            return null;
        }

        const { tipPayoutSettings, preview, tipPayoutMarkAllLoading, tipPayoutMarkAllError } = this.props;
        const { totalUnpaid } = this.getManagerSummaryAmounts(tipPayoutSettings, preview);

        return (
            <Modal
                isOpen
                toggle={this.closeMarkAllPaidConfirmModal}
                backdrop={tipPayoutMarkAllLoading ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={tipPayoutMarkAllLoading ? undefined : this.closeMarkAllPaidConfirmModal}
                    className={s.confirmModalHeader}
                >
                    Mark all outstanding tips as paid
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        This updates employee tip records only. It does not move Stripe funds.
                        Use this when every employee has already been paid and you need the system
                        to reflect that for the selected work period.
                    </p>
                    <div className={s.confirmSummary}>
                        <div>
                            <span>Outstanding tips &amp; gratuity</span>
                            <strong>{this.formatCurrency(totalUnpaid)}</strong>
                        </div>
                    </div>
                    <FormGroup className={s.markAllPaidReasonGroup}>
                        <Label for="markAllPaidReason">Reason (required)</Label>
                        <Input
                            id="markAllPaidReason"
                            type="textarea"
                            rows={3}
                            value={this.state.markAllPaidReason}
                            onChange={this.handleMarkAllPaidReasonChange}
                            disabled={tipPayoutMarkAllLoading}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    {tipPayoutMarkAllError ? <p className={s.errorText}>{tipPayoutMarkAllError}</p> : null}
                    <div className={s.confirmModalActions}>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.closeMarkAllPaidConfirmModal}
                            disabled={tipPayoutMarkAllLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmMarkAllOutstandingTipsPaid}
                            disabled={tipPayoutMarkAllLoading}
                        >
                            {tipPayoutMarkAllLoading ? 'Marking…' : 'Mark All As Paid'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPayRecipientConfirmModal() {
        const { payRecipientConfirm } = this.state;
        const { tipPayoutDirectPaying, tipPayoutDirectPayError } = this.props;
        if (!payRecipientConfirm) {
            return null;
        }

        return (
            <Modal
                isOpen
                toggle={tipPayoutDirectPaying ? undefined : this.closePayRecipientConfirmModal}
                backdrop={tipPayoutDirectPaying ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={tipPayoutDirectPaying ? undefined : this.closePayRecipientConfirmModal}
                    className={s.confirmModalHeader}
                >
                    Pay tips to employee
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Pay cleared tips and gratuity directly to{' '}
                        <strong>{payRecipientConfirm.name}</strong>&apos;s Stripe account.
                    </p>
                    <div className={s.confirmSummary}>
                        <div>
                            <span>Employee</span>
                            <strong>{payRecipientConfirm.name}</strong>
                        </div>
                        <div>
                            <span>Cleared</span>
                            <strong>{this.formatTipsOwedDisplay(payRecipientConfirm.cleared)}</strong>
                        </div>
                        <div>
                            <span>Owed</span>
                            <strong>{this.formatTipsOwedDisplay(payRecipientConfirm.tipsOwed)}</strong>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    {tipPayoutDirectPayError ? <p className={s.errorText}>{tipPayoutDirectPayError}</p> : null}
                    <div className={s.confirmModalActions}>
                        <Button
                            color="secondary"
                            type="button"
                            outline
                            onClick={this.closePayRecipientConfirmModal}
                            disabled={tipPayoutDirectPaying}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmPayRecipient}
                            disabled={tipPayoutDirectPaying}
                        >
                            {tipPayoutDirectPaying ? 'Paying…' : 'Confirm pay'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPendingTipsBreakdownModal() {
        const { pendingTipsBreakdownOpen } = this.state;
        if (!pendingTipsBreakdownOpen) {
            return null;
        }

        const {
            tipPayoutSettings,
            preview,
            tipPayoutPendingBreakdownLoading,
            tipPayoutPendingBreakdownError,
        } = this.props;
        // Header matches the Pending summary row; rows explain that amount (incl. residual).
        const pending = this.getManagerSummaryAmounts(tipPayoutSettings, preview).pending;
        const rows = this.getPendingTipsBreakdownRows();
        const rowsTotal = rows.reduce((sum, row) => sum + toNumber(row.amountOwed), 0);
        const displayTotal = pending > 0.001 ? pending : rowsTotal;

        return (
            <Modal
                isOpen
                toggle={this.closePendingTipsBreakdownModal}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={this.closePendingTipsBreakdownModal}
                    className={s.confirmModalHeader}
                >
                    Pending Tips &amp; Gratuity
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Tips and gratuity still held on the platform or merchant account that are not
                        yet paid out — awaiting cleared / available funds. Total pending:{' '}
                        <strong>{this.formatCurrency(displayTotal)}</strong>
                    </p>
                    {tipPayoutPendingBreakdownError ? (
                        <p className={s.errorText}>{tipPayoutPendingBreakdownError}</p>
                    ) : null}
                    {tipPayoutPendingBreakdownLoading && rows.length === 0 ? (
                        <p className={s.noteText}>Loading breakdown…</p>
                    ) : rows.length > 0 ? (
                        <div className={s.pendingTipsBreakdownTableWrap}>
                            <table className={s.pendingTipsBreakdownTable}>
                                <thead>
                                    <tr>
                                        <th scope="col">Work period</th>
                                        <th scope="col">Employee</th>
                                        <th scope="col">Pending</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={`${row.userId}-${row.workPeriodId || 'none'}`}>
                                            <td>{this.formatWorkPeriodDate(row.workPeriodDate)}</td>
                                            <td>{this.formatPendingBreakdownEmployeeName(row)}</td>
                                            <td>{this.formatCurrency(row.amountOwed)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : !tipPayoutPendingBreakdownLoading ? (
                        <p className={s.noteText}>
                            No pending tip breakdown is available for the selected work period.
                        </p>
                    ) : null}
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.closePendingTipsBreakdownModal}
                        >
                            Close
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPaidToManagerBreakdownModal() {
        const { paidToManagerBreakdownOpen } = this.state;
        if (!paidToManagerBreakdownOpen) {
            return null;
        }

        const {
            tipPayoutSettings,
            tipPayoutPaidToManagerBreakdownLoading,
            tipPayoutPaidToManagerBreakdownError,
        } = this.props;
        const paidToManager = this.getTipsAndGratuityPaidToManagerAmount(tipPayoutSettings);
        const rows = this.getPaidToManagerBreakdownRows();
        const rowsTotal = rows.reduce((sum, row) => sum + toNumber(row.amountPaid), 0);
        const displayTotal = paidToManager > 0.001 ? paidToManager : rowsTotal;

        return (
            <Modal
                isOpen
                toggle={this.closePaidToManagerBreakdownModal}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={this.closePaidToManagerBreakdownModal}
                    className={s.confirmModalHeader}
                >
                    Tips and gratuity paid to manager
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Tips and gratuity already paid to the designated manager that have not been
                        marked as paid in the system yet. Total:{' '}
                        <strong>{this.formatCurrency(displayTotal)}</strong>
                    </p>
                    {tipPayoutPaidToManagerBreakdownError ? (
                        <p className={s.errorText}>{tipPayoutPaidToManagerBreakdownError}</p>
                    ) : null}
                    {tipPayoutPaidToManagerBreakdownLoading && rows.length === 0 ? (
                        <p className={s.noteText}>Loading breakdown…</p>
                    ) : rows.length > 0 ? (
                        <div className={s.pendingTipsBreakdownTableWrap}>
                            <table className={s.pendingTipsBreakdownTable}>
                                <thead>
                                    <tr>
                                        <th scope="col">Work period</th>
                                        <th scope="col">Employee</th>
                                        <th scope="col">Paid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={`${row.userId}-${row.workPeriodId || 'none'}`}>
                                            <td>{this.formatWorkPeriodDate(row.workPeriodDate)}</td>
                                            <td>{this.formatPendingBreakdownEmployeeName(row)}</td>
                                            <td>{this.formatCurrency(row.amountPaid)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : !tipPayoutPaidToManagerBreakdownLoading ? (
                        <p className={s.noteText}>
                            No tips paid to manager were found for the selected work period.
                        </p>
                    ) : null}
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.closePaidToManagerBreakdownModal}
                        >
                            Close
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderReleasableTipsBreakdownModal() {
        const { releasableTipsBreakdownOpen, payingReleasableRowKey, releasablePartialPayNote } = this.state;
        if (!releasableTipsBreakdownOpen) {
            return null;
        }

        const {
            tipPayoutSettings,
            preview,
            tipPayoutReleasableBreakdownLoading,
            tipPayoutReleasableBreakdownError,
            canExecuteTipPayoutToRecipient,
        } = this.props;
        const releasable = this.getTipsAvailableToReleaseAmount(
            preview,
            tipPayoutSettings,
            { forManagerSummary: true },
        );
        const rows = this.getReleasableTipsBreakdownRows();
        const rowsTotal = rows.reduce((sum, row) => sum + toNumber(row.amountReleasable), 0);
        // Prefer the sum of listed rows so the modal total always matches who/what is shown.
        const displayTotal = rows.length > 0 ? rowsTotal : releasable;

        return (
            <Modal
                isOpen
                toggle={payingReleasableRowKey ? undefined : this.closeReleasableTipsBreakdownModal}
                backdrop={payingReleasableRowKey ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={payingReleasableRowKey ? undefined : this.closeReleasableTipsBreakdownModal}
                    className={s.confirmModalHeader}
                >
                    {this.props.t('payoutActions.availableTips')} breakdown
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Unpaid tips and gratuity owed to each employee for their work period, limited to
                        amounts cleared and available to pay now. Total available:{' '}
                        <strong>{this.formatCurrency(displayTotal)}</strong>
                    </p>
                    {tipPayoutReleasableBreakdownError ? (
                        <p className={s.errorText}>{tipPayoutReleasableBreakdownError}</p>
                    ) : null}
                    {tipPayoutReleasableBreakdownLoading && rows.length === 0 ? (
                        <p className={s.noteText}>Loading breakdown…</p>
                    ) : rows.length > 0 ? (
                        <>
                            <div className={s.pendingTipsBreakdownTableWrap}>
                                <table className={s.pendingTipsBreakdownTable}>
                                    <thead>
                                        <tr>
                                            <th scope="col">Work period</th>
                                            <th scope="col">Employee</th>
                                            <th scope="col">{this.props.t('payoutActions.availableTips')}</th>
                                            {canExecuteTipPayoutToRecipient ? (
                                                <th scope="col" className={s.payRecipientsActionCol}>Pay</th>
                                            ) : null}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => {
                                            const rowKey = this.getReleasableRowKey(row);
                                            const isPayingRow = payingReleasableRowKey === rowKey;
                                            const canPayRow = toNumber(row.amountReleasable) > 0 && row.canPayDirect;

                                            return (
                                                <tr key={rowKey}>
                                                    <td>{this.formatWorkPeriodDate(row.workPeriodDate)}</td>
                                                    <td>{this.formatPendingBreakdownEmployeeName(row)}</td>
                                                    <td>{this.formatCurrency(row.amountReleasable)}</td>
                                                    {canExecuteTipPayoutToRecipient ? (
                                                        <td className={s.payRecipientsActionCol}>
                                                            {canPayRow ? (
                                                                <Button
                                                                    color="primary"
                                                                    size="sm"
                                                                    type="button"
                                                                    className={s.payRecipientsPayBtn}
                                                                    disabled={this.isTipPayoutInProgress()}
                                                                    onClick={() => this.handlePayReleasableBreakdownRow(row)}
                                                                >
                                                                    {isPayingRow ? 'Paying…' : 'Pay'}
                                                                </Button>
                                                            ) : (
                                                                <span className={s.noteText}>—</span>
                                                            )}
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {releasablePartialPayNote ? (
                                <p className={s.releasableBreakdownPartialNote}>{releasablePartialPayNote}</p>
                            ) : null}
                        </>
                    ) : !tipPayoutReleasableBreakdownLoading ? (
                        <p className={s.noteText}>
                            No releasable tip breakdown is available for the selected work period.
                        </p>
                    ) : null}
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.closeReleasableTipsBreakdownModal}
                            disabled={!!payingReleasableRowKey}
                        >
                            Close
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderMerchantConfigurationSection() {
        const { canConfigureReserve, canViewTipPayoutSettings } = this.props;
        if (!canConfigureReserve && !canViewTipPayoutSettings) {
            return null;
        }

        const widgetTitle = canConfigureReserve
            ? 'Merchant Connected Account Reserve'
            : 'Tip Payout';

        return (
            <Widget title={widgetTitle} className={s.sectionWidget}>
                {canConfigureReserve ? (
                    <p className={s.intro}>
                        Configure optional flat, percentage{this.isTipPayoutDisabled() ? '' : ', Reserve Tips & Gratuity'}, and Reserve Tax holds on the merchant connected account.
                        Saving creates a Stripe <strong>ReserveHold</strong> so reserved funds cannot be paid out from the Stripe Dashboard.
                        Use <strong>Release Reserve</strong> to turn off all holds and restore payout eligibility.
                    </p>
                ) : null}

                <div
                    className={`${s.reserveConfigRow}${
                        !canConfigureReserve ? ` ${s.reserveConfigRowSingle}` : ''
                    }`}
                >
                    {canConfigureReserve ? (
                        <div className={s.reserveConfigCol}>
                            {this.renderReserveControlsCard()}
                        </div>
                    ) : null}
                    {canViewTipPayoutSettings ? (
                        <div className={s.reserveConfigCol}>
                            {this.renderTipPayoutPanel()}
                        </div>
                    ) : null}
                </div>
            </Widget>
        );
    }

    renderReserveControlsCard() {
        const reserveForm = { ...EMPTY_RESERVE_FORM, ...this.state.reserveForm };
        const { reserveSaving, reserveLoading, reserveLoadError } = this.props;

        return (
            <div className={s.reserveControlsCard}>
                <div className={s.reserveControlsHeader}>Reserve Controls</div>
                {reserveLoadError ? <p className={s.errorText}>{reserveLoadError}</p> : null}
                {reserveLoading && !reserveLoadError ? (
                    <p className={s.noteText}>Loading reserve settings…</p>
                ) : null}
                <div className={s.reserveControlsBody}>
                    <Form onSubmit={this.handleSaveReserve}>
                            <FormGroup check className={s.toggleRow}>
                                <CustomInput
                                    type="switch"
                                    id="flatReserveEnabled"
                                    label="Flat amount reserve"
                                    checked={reserveForm.flatReserveEnabled}
                                    onChange={this.handleReserveToggle('flatReserveEnabled')}
                                />
                            </FormGroup>
                            {reserveForm.flatReserveEnabled ? (
                                <FormGroup>
                                    <Label for="flatReserveAmount">Flat amount</Label>
                                    <Input
                                        id="flatReserveAmount"
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        placeholder="0.00"
                                        value={reserveForm.flatReserveAmount ?? ''}
                                        onChange={this.handleReserveInput('flatReserveAmount')}
                                    />
                                </FormGroup>
                            ) : null}

                            <FormGroup check className={s.toggleRow}>
                                <CustomInput
                                    type="switch"
                                    id="percentReserveEnabled"
                                    label="Percentage reserve"
                                    checked={reserveForm.percentReserveEnabled}
                                    onChange={this.handleReserveToggle('percentReserveEnabled')}
                                />
                            </FormGroup>
                            {reserveForm.percentReserveEnabled ? (
                                <FormGroup>
                                    <Label for="percentReserveValue">Percentage of available balance</Label>
                                    <Input
                                        id="percentReserveValue"
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        placeholder="0"
                                        value={reserveForm.percentReserveValue ?? ''}
                                        onChange={this.handleReserveInput('percentReserveValue')}
                                    />
                                </FormGroup>
                            ) : null}

                            {!this.isTipPayoutDisabled() ? (
                            <FormGroup check className={s.toggleRow}>
                                <CustomInput
                                    type="switch"
                                    id="reserveTipsEnabled"
                                    label="Reserve Tips & Gratuity"
                                    checked={reserveForm.reserveTipsEnabled}
                                    onChange={this.handleReserveToggle('reserveTipsEnabled')}
                                />
                            </FormGroup>
                            ) : null}
                            {!this.isTipPayoutDisabled() && reserveForm.reserveTipsEnabled ? (
                                <p className={s.noteText}>
                                    Unpaid tips and gratuity not successfully auto-transferred to employees will be held for manual payout.
                                </p>
                            ) : null}

                            <FormGroup check className={s.toggleRow}>
                                <CustomInput
                                    type="switch"
                                    id="reserveTaxEnabled"
                                    label="Reserve Tax"
                                    checked={reserveForm.reserveTaxEnabled}
                                    onChange={this.handleReserveToggle('reserveTaxEnabled')}
                                />
                            </FormGroup>
                            {reserveForm.reserveTaxEnabled ? (
                                <p className={s.noteText}>
                                    Collected sales tax will be held on the connected account so it is not paid out with normal merchant payouts.
                                </p>
                            ) : null}

                            {this.renderStripeHoldStatus()}

                            <div className={s.actionRow}>
                                <Button
                                    color="secondary"
                                    type="button"
                                    outline
                                    disabled={reserveSaving}
                                    onClick={this.openReleaseReserveModal}
                                >
                                    Release Reserve
                                </Button>
                                <Button color="primary" type="submit" disabled={reserveSaving}>
                                    {reserveSaving ? 'Saving…' : 'Save reserve'}
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
        );
    }

    renderStripeHoldStatus() {
        const { preview } = this.props;
        if (!preview) return null;

        const held = Number(preview.stripeHeldAmount ?? 0);
        const reserved = Number(preview.totalReserved ?? 0);
        const enforced = !!preview.stripeHoldEnforced;
        const syncError = preview.stripeHoldSyncError || '';

        if (reserved <= 0 && held <= 0 && !syncError) {
            return null;
        }

        return (
            <div className={s.stripeHoldStatus}>
                <p className={s.noteText}>
                    Stripe ReserveHold: {this.formatCurrency(held)}
                    {reserved > 0 ? ` of ${this.formatCurrency(reserved)} target` : ''}
                    {enforced ? ' — enforced' : ' — not fully enforced'}
                </p>
                {syncError ? <p className={s.errorText}>{syncError}</p> : null}
            </div>
        );
    }

    renderReleaseReserveModal() {
        if (!this.state.releaseReserveConfirmOpen) {
            return null;
        }

        const { preview, reserveSaving } = this.props;
        const totalReserved = preview?.totalReserved ?? 0;
        const settledBalance = preview?.availableBalance ?? 0;
        const availableAfterRelease = Math.max(0, settledBalance);

        return (
            <Modal
                isOpen
                toggle={this.closeReleaseReserveModal}
                backdrop={reserveSaving ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={reserveSaving ? undefined : this.closeReleaseReserveModal}
                    className={s.confirmModalHeader}
                >
                    Release reserve
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        This turns off all merchant connected account reserve rules — flat amount,
                        percentage, Reserve Tips &amp; Gratuity, and Reserve Tax — releases any Stripe ReserveHolds,
                        and restores automatic payout eligibility where applicable.
                    </p>
                    <ul className={s.releaseReserveList}>
                        <li>
                            <strong>Reserved hold</strong> will be reduced from {this.formatCurrency(totalReserved)} to $0.00.
                        </li>
                        <li>
                            <strong>{this.props.t('payoutActions.availablePayout')}</strong> will reflect the full settled balance
                            ({this.formatCurrency(availableAfterRelease)}), subject to your Stripe account balance.
                        </li>
                        <li>
                            Stripe automatic payouts to the merchant bank account will resume if they were paused for reserve.
                        </li>
                    </ul>
                    <p className={s.confirmFeeNote}>
                        This does not send funds to the bank or pay employees. Use <strong>Pay out</strong> to initiate a
                        merchant payout, or wait for the restored Stripe payout schedule.
                        Unpaid tip and gratuity obligations to staff are unchanged.
                    </p>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.closeReleaseReserveModal}
                            disabled={reserveSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmReleaseReserve}
                            disabled={reserveSaving}
                        >
                            {reserveSaving ? 'Releasing…' : 'Release Reserve'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPayTipsConfirmModal() {
        if (!this.state.payTipsConfirmOpen) {
            return null;
        }

        const {
            preview,
            tipPayoutSettings,
            tipPayoutPaying,
            tipPayoutPayError,
        } = this.props;
        const designatedRecipient = tipPayoutSettings?.designatedRecipient;
        const availableToRelease = this.getTipsAvailableToReleaseAmount(
            preview,
            tipPayoutSettings,
            { forManagerSummary: true },
        );
        const { pending } = this.getManagerSummaryAmounts(tipPayoutSettings, preview);
        const totalUnpaid = availableToRelease + pending;
        const releaseAmount = Math.min(availableToRelease, totalUnpaid > 0 ? totalUnpaid : availableToRelease);
        const recipientLabel = designatedRecipient
            ? this.formatRecipientLabel(designatedRecipient)
            : 'Designated recipient';

        return (
            <Modal
                isOpen
                toggle={this.closePayTipsConfirmModal}
                backdrop={tipPayoutPaying ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={tipPayoutPaying ? undefined : this.closePayTipsConfirmModal}
                    className={s.confirmModalHeader}
                >
                    Confirm tip release to manager
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        Release cleared unpaid tips and gratuity to the designated manager via Stripe Connect.
                        Tips already paid at sale time to Tips On employees are not included.
                    </p>
                    <div className={s.confirmSummary}>
                        <div>
                            <span>Recipient</span>
                            <strong>{recipientLabel}</strong>
                        </div>
                        <div>
                            <span>{this.props.t('payoutActions.availableTips')} to release</span>
                            <strong>{this.formatCurrency(releaseAmount)}</strong>
                        </div>
                        {totalUnpaid > releaseAmount + 0.01 ? (
                            <div>
                                <span>Unpaid tips &amp; gratuity (selected period)</span>
                                <strong>{this.formatCurrency(totalUnpaid)}</strong>
                            </div>
                        ) : null}
                        {pending > 0 ? (
                            <div>
                                <span>Pending (awaiting cleared funds)</span>
                                <strong>{this.formatCurrency(pending)}</strong>
                            </div>
                        ) : null}
                    </div>
                    <p className={s.confirmFeeNote}>
                        {releaseAmount < totalUnpaid - 0.01
                            ? 'Only cleared funds will be released. Remaining unpaid obligations stay open until more funds settle or you release again.'
                            : 'Cleared obligations for the selected work period will be marked paid manually after the transfer succeeds.'}
                    </p>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    {tipPayoutPayError ? <p className={s.errorText}>{tipPayoutPayError}</p> : null}
                    <div className={s.confirmModalActions}>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.closePayTipsConfirmModal}
                            disabled={tipPayoutPaying}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmPayTipsToRecipient}
                            disabled={tipPayoutPaying}
                        >
                            {tipPayoutPaying ? 'Processing…' : 'Release Tips'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderTaxCollectedResetConfirmModal() {
        if (!this.state.taxCollectedResetConfirmOpen) {
            return null;
        }

        const amount = toNumber(this.props.preview?.taxCollectedAmount);
        const busy = this.state.taxCollectedResetBusy;

        return (
            <Modal
                isOpen
                toggle={this.closeTaxCollectedResetConfirm}
                backdrop={busy ? 'static' : true}
                className={s.confirmModal}
                centered
            >
                <ModalHeader
                    toggle={busy ? undefined : this.closeTaxCollectedResetConfirm}
                    className={s.confirmModalHeader}
                >
                    Reset Tax Collected
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        This records a remittance for the full Tax Collected amount and sets the dashboard
                        pool to <strong>$0.00</strong>. Use only after this tax has been remitted or otherwise cleared.
                    </p>
                    <div className={s.confirmSummary}>
                        <div>
                            <span>Amount to clear</span>
                            <strong>{this.formatCurrency(amount)}</strong>
                        </div>
                        <div>
                            <span>Collected since</span>
                            <strong>{this.formatTaxCollectedSinceDate()}</strong>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.closeTaxCollectedResetConfirm}
                            disabled={busy}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmTaxCollectedReset}
                            disabled={busy || amount <= 0}
                        >
                            {busy ? 'Resetting…' : 'Reset to $0'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPayoutSuccessModal() {
        const { payoutSuccess } = this.state;
        if (!payoutSuccess) {
            return null;
        }

        const isInstant = payoutSuccess.method === 'instant';
        const amount = Number(payoutSuccess.amount) || 0;
        const fee = Number(payoutSuccess.estimatedFee) || 0;
        const netAmount = Math.max(0, amount - fee);
        const payoutId = payoutSuccess.payoutId || '';

        return (
            <Modal
                isOpen
                toggle={this.closePayoutSuccessModal}
                className={`${s.confirmModal} ${s.payoutSuccessModal}`}
                centered
            >
                <ModalBody className={`${s.confirmModalBody} ${s.payoutSuccessBody}`}>
                    <div className={s.payoutSuccessIcon} aria-hidden="true">
                        <i className="fa fa-check" />
                    </div>
                    <h2 className={s.payoutSuccessTitle}>{this.props.t('payoutActions.payoutSuccessful')}</h2>
                    <p className={s.payoutSuccessSubtitle}>
                        {isInstant ? 'Instant payout initiated.' : 'Standard payout initiated.'}
                    </p>
                    <div className={s.payoutSuccessDetails}>
                        <div>
                            <span>Amount</span>
                            <strong>{this.formatCurrency(amount)}</strong>
                        </div>
                        <div>
                            <span>Method</span>
                            <strong>{isInstant ? 'Instant' : 'Standard'}</strong>
                        </div>
                        {isInstant && fee > 0 ? (
                            <div>
                                <span>Estimated fee</span>
                                <strong>{this.formatCurrency(fee)}</strong>
                            </div>
                        ) : null}
                        {isInstant && fee > 0 ? (
                            <div>
                                <span>Estimated received</span>
                                <strong>{this.formatCurrency(netAmount)}</strong>
                            </div>
                        ) : null}
                        {payoutId ? (
                            <div className={s.payoutSuccessIdRow}>
                                <span>Payout ID</span>
                                <strong>{payoutId}</strong>
                            </div>
                        ) : null}
                    </div>
                    <Button
                        color="primary"
                        type="button"
                        className={s.payoutSuccessDoneBtn}
                        onClick={this.closePayoutSuccessModal}
                    >
                        Done
                    </Button>
                </ModalBody>
            </Modal>
        );
    }

    renderPayoutConfirmModal() {
        const { payoutConfirm } = this.state;
        if (!payoutConfirm) {
            return null;
        }

        const { preview, payoutCreating, payoutError } = this.props;
        const method = payoutConfirm.method || 'standard';
        const parsedAmount = this.getPayoutConfirmAmount();
        const summaryAmount = parsedAmount ?? 0;
        const maxAmount = this.getPayoutEligibleAmount(method, preview);
        const instantAvailable = !!preview?.instantPayoutAvailable;
        const isInstant = method === 'instant';
        const fee = isInstant && parsedAmount !== null
            ? calculatePlatformInstantFee(parsedAmount, preview)
            : 0;
        const netAmount = Math.max(0, summaryAmount - fee);
        const feePercent = toNumber(preview?.instantPayoutFeePercent, 2);
        const feeFixed = toNumber(preview?.instantPayoutFeeFixed, 0);
        const hasAvailableBalance = maxAmount > 0;

        return (
            <Modal
                isOpen
                toggle={this.closePayoutConfirmModal}
                backdrop={payoutCreating ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={payoutCreating ? undefined : this.closePayoutConfirmModal}
                    className={s.confirmModalHeader}
                >
                    Confirm payout
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <Form
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!payoutCreating) {
                                this.confirmPayoutContinue();
                            }
                        }}
                    >
                        <p className={s.confirmIntro}>
                            Choose the payout amount and method for the merchant connected account.
                            Reserved funds will not be included.
                        </p>
                        <FormGroup className={s.confirmAmountGroup}>
                            <Label for="payoutConfirmAmount">Payout amount</Label>
                            <Input
                                id="payoutConfirmAmount"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                placeholder="0.00"
                                value={payoutConfirm.amountInput ?? ''}
                                onChange={this.handlePayoutConfirmAmountChange}
                                disabled={payoutCreating}
                            />
                            <small className={s.confirmAmountHint}>
                                Maximum available: {this.formatCurrency(maxAmount)}
                            </small>
                            {!hasAvailableBalance ? (
                                <p className={s.errorText}>
                                    No funds are available for {isInstant ? 'instant' : 'standard'} payout.
                                </p>
                            ) : null}
                        </FormGroup>
                        <FormGroup className={s.confirmAmountGroup}>
                            <Label for="payoutConfirmMethod">Payout method</Label>
                            <Input
                                id="payoutConfirmMethod"
                                type="select"
                                value={method}
                                onChange={this.handlePayoutConfirmMethodChange}
                                disabled={payoutCreating}
                            >
                                <option value="standard">Standard</option>
                                <option value="instant" disabled={!instantAvailable}>Instant</option>
                            </Input>
                            {!instantAvailable ? (
                                <small className={s.confirmAmountHint}>
                                    Instant payout is not available for this merchant account.
                                </small>
                            ) : null}
                        </FormGroup>
                        <div className={s.confirmSummary}>
                            <div>
                                <span>Payout amount</span>
                                <strong>{this.formatCurrency(summaryAmount)}</strong>
                            </div>
                            <div>
                                <span>Method</span>
                                <strong>{isInstant ? 'Instant' : 'Standard'}</strong>
                            </div>
                            {isInstant ? (
                                <div>
                                    <span>Instant payout fee</span>
                                    <strong>{this.formatCurrency(fee)}</strong>
                                </div>
                            ) : null}
                            {isInstant ? (
                                <div className={s.confirmSummaryTotal}>
                                    <span>Estimated received</span>
                                    <strong>{this.formatCurrency(netAmount)}</strong>
                                </div>
                            ) : null}
                        </div>
                        {isInstant ? (
                            <p className={s.confirmFeeNote}>
                                {feeFixed > 0
                                    ? `Platform instant payout fee: ${feePercent}% + ${this.formatCurrency(feeFixed)} per payout. The fee is deducted from the payout amount.`
                                    : `Platform instant payout fee: ${feePercent}%. The fee is deducted from the payout amount.`}
                            </p>
                        ) : (
                            <p className={s.confirmFeeNote}>
                                Standard payouts have no platform instant payout fee. Funds typically arrive on the normal bank schedule.
                            </p>
                        )}
                    </Form>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    {payoutError ? <p className={s.errorText}>{payoutError}</p> : null}
                    <div className={s.confirmModalActions}>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.closePayoutConfirmModal}
                            disabled={payoutCreating}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={this.confirmPayoutContinue}
                            disabled={payoutCreating}
                        >
                            {payoutCreating ? 'Processing…' : 'Payout Continue'}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    renderPlatformFundingSummary() {
        const { tipFundingSummary, tipFundingSummaryLoading, tipFundingSummaryError } = this.props;

        if (!isRebelityPlatformAdmin(this.props)) {
            return null;
        }

        return (
            <Widget title="Platform tip liquidity" className={s.sectionWidget}>
                <div className={s.fundingSummary}>
                    <div className={s.sectionHeader}>
                        <span className={s.sectionTitle}>Rebelity platform balances</span>
                        <button
                            type="button"
                            className={s.refreshBtn}
                            onClick={() => this.props.dispatch(getTipFundingSummaryRequest())}
                            disabled={tipFundingSummaryLoading}
                        >
                            {tipFundingSummaryLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>

                    {tipFundingSummaryError ? <p className={s.errorText}>{tipFundingSummaryError}</p> : null}
                    {tipFundingSummaryLoading && !tipFundingSummary ? (
                        <p className={s.noteText}>Loading platform balances…</p>
                    ) : null}

                    {tipFundingSummary ? (
                        <>
                            <div className={`${s.detailsGrid} ${s.previewGrid}`}>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>Available</span>
                                    <span className={s.detailValue}>
                                        {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'PlatformAvailable', 'platformAvailable'))}
                                    </span>
                                </div>
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>Pending</span>
                                    <span className={s.detailValue}>
                                        {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'PlatformPending', 'platformPending'))}
                                    </span>
                                </div>
                                {!this.isTipPayoutDisabled() ? (
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>Open obligations</span>
                                    <span className={s.detailValue}>
                                        {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'OpenObligationAmount', 'openObligationAmount'))}
                                    </span>
                                </div>
                                ) : null}
                                {!this.isTipPayoutDisabled() ? (
                                <div className={s.detailItem}>
                                    <span className={s.detailLabel}>Held</span>
                                    <span className={s.detailValue}>
                                        {this.formatCurrency(this.getFundingNumber(tipFundingSummary, 'HeldObligationAmount', 'heldObligationAmount'))}
                                    </span>
                                </div>
                                ) : null}
                            </div>
                            {tipFundingSummary.LiquidityNote || tipFundingSummary.liquidityNote ? (
                                <p className={s.noteText}>
                                    {tipFundingSummary.LiquidityNote || tipFundingSummary.liquidityNote}
                                </p>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </Widget>
        );
    }

    // ── Admin Force Reclaim (Rebelity Admin only, additive) ────────────────

    handleForceReclaimInput = field => (event) => {
        this.setState({
            [field]: event.target.value,
            forceReclaimPreview: null,
            forceReclaimPreviewError: '',
        });
    };

    buildForceReclaimScope() {
        const transferIds = String(this.state.forceReclaimTransferIds || '')
            .split(/[\s,;]+/)
            .map(v => parseInt(v, 10))
            .filter(v => Number.isFinite(v) && v > 0);
        const userId = parseInt(this.state.forceReclaimUserId, 10);
        const workPeriodId = parseInt(this.state.forceReclaimWorkPeriodId, 10);

        return {
            transferIds,
            userId: Number.isFinite(userId) && userId > 0 ? userId : null,
            workPeriodId: Number.isFinite(workPeriodId) && workPeriodId > 0 ? workPeriodId : null,
            reason: String(this.state.forceReclaimReason || '').trim(),
        };
    }

    canRunForceReclaim() {
        const scope = this.buildForceReclaimScope();
        const hasScope = scope.transferIds.length > 0 || scope.userId || scope.workPeriodId;
        return hasScope && scope.reason.length >= 5;
    }

    runForceReclaimPreview = () => {
        const scope = this.buildForceReclaimScope();
        this.setState({ forceReclaimRunning: true, forceReclaimPreview: null, forceReclaimPreviewError: '' });
        this.props.dispatch(adminForceReclaimRequest({ ...scope, dryRun: true })).then((res) => {
            this.setState({
                forceReclaimRunning: false,
                forceReclaimPreview: res.result || null,
                forceReclaimPreviewError: res.success ? '' : (res.message || 'Preview failed.'),
            });
        });
    };

    openForceReclaimConfirm = () => this.setState({ forceReclaimConfirmOpen: true });

    closeForceReclaimConfirm = () => {
        if (this.state.forceReclaimRunning) return;
        this.setState({ forceReclaimConfirmOpen: false });
    };

    executeForceReclaim = () => {
        const scope = this.buildForceReclaimScope();
        this.setState({ forceReclaimRunning: true });
        this.props.dispatch(adminForceReclaimRequest({ ...scope, dryRun: false })).then((res) => {
            this.setState({
                forceReclaimRunning: false,
                forceReclaimConfirmOpen: false,
                forceReclaimPreview: res.result || null,
                forceReclaimPreviewError: res.success ? '' : (res.message || 'Force reclaim failed.'),
            });
            if (res.success) {
                this.setState({ forceReclaimAudit: null });
            }
        });
    };

    toggleForceReclaimAudit = () => {
        const opening = !this.state.forceReclaimAuditOpen;
        this.setState({ forceReclaimAuditOpen: opening });
        if (opening && !this.state.forceReclaimAudit) {
            this.props.dispatch(loadForceReclaimAuditRequest(20)).then((res) => {
                this.setState({ forceReclaimAudit: res.records || [] });
            });
        }
    };

    describeForceReclaimOutcome(outcome) {
        switch (outcome) {
            case 'would_reclaim': return 'Will reclaim';
            case 'reclaimed': return 'Reclaimed';
            case 'already_on_platform': return 'Already on platform';
            case 'skipped_completed_payout': return 'Skipped — already paid to employee';
            case 'skipped_zero_amount': return 'Skipped — zero amount';
            case 'failed': return 'Failed';
            default: return outcome || '—';
        }
    }

    renderAdminForceReclaimSection() {
        if (!this.props.isRebelityAdmin || this.isTipPayoutDisabled()) {
            return null;
        }

        const {
            forceReclaimPreview: preview,
            forceReclaimPreviewError: previewError,
            forceReclaimRunning: running,
            forceReclaimAuditOpen: auditOpen,
            forceReclaimAudit: audit,
        } = this.state;

        const reclaimableCount = preview
            ? preview.rows.filter(r => r.outcome === 'would_reclaim' || r.outcome === 'reclaimed').length
            : 0;

        return (
            <Widget title="Force Reclaim (Admin)" className={s.sectionWidget}>
                <p className={s.intro}>
                    Rebelity Admin override: reverse tip &amp; gratuity funds from the merchant Stripe account back to
                    the platform <strong>regardless of the normal payout guards</strong> (held, skipped, failed, or scoped-out rows).
                    Rows already on the platform and tips already paid to employees are never touched. Reclaimed rows are
                    frozen as <code>admin_reclaimed</code> until an admin acts on them. Every run is audited.
                </p>

                <div className={s.forceReclaimForm}>
                    <FormGroup>
                        <Label for="forceReclaimTransferIds">Transfer IDs (comma-separated, optional)</Label>
                        <Input
                            id="forceReclaimTransferIds"
                            type="text"
                            autoComplete="off"
                            placeholder="e.g. 123, 124, 125"
                            value={this.state.forceReclaimTransferIds}
                            onChange={this.handleForceReclaimInput('forceReclaimTransferIds')}
                        />
                    </FormGroup>
                    <div className={s.forceReclaimFilterRow}>
                        <FormGroup>
                            <Label for="forceReclaimUserId">Employee user ID (optional)</Label>
                            <Input
                                id="forceReclaimUserId"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="e.g. 45"
                                value={this.state.forceReclaimUserId}
                                onChange={this.handleForceReclaimInput('forceReclaimUserId')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="forceReclaimWorkPeriodId">Work period ID (optional)</Label>
                            <Input
                                id="forceReclaimWorkPeriodId"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="e.g. 678"
                                value={this.state.forceReclaimWorkPeriodId}
                                onChange={this.handleForceReclaimInput('forceReclaimWorkPeriodId')}
                            />
                        </FormGroup>
                    </div>
                    <FormGroup>
                        <Label for="forceReclaimReason">Reason (required, recorded in the audit log)</Label>
                        <Input
                            id="forceReclaimReason"
                            type="text"
                            autoComplete="off"
                            placeholder="Why are you overriding the normal payout process?"
                            value={this.state.forceReclaimReason}
                            onChange={this.handleForceReclaimInput('forceReclaimReason')}
                        />
                    </FormGroup>

                    <div className={s.actionRow}>
                        <Button
                            color="secondary"
                            type="button"
                            outline
                            onClick={this.toggleForceReclaimAudit}
                        >
                            {auditOpen ? 'Hide history' : 'View history'}
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            disabled={running || !this.canRunForceReclaim()}
                            onClick={this.runForceReclaimPreview}
                        >
                            {running ? 'Working…' : 'Preview (dry run)'}
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            disabled={running || !preview || preview.dryRun === false || reclaimableCount === 0}
                            onClick={this.openForceReclaimConfirm}
                        >
                            Force reclaim…
                        </Button>
                    </div>
                    {!this.canRunForceReclaim() ? (
                        <p className={s.noteText}>
                            Enter transfer IDs, an employee ID, or a work period ID — plus a reason — to enable the preview.
                        </p>
                    ) : null}
                </div>

                {previewError ? <p className={s.errorText}>{previewError}</p> : null}

                {preview ? (
                    <div className={s.forceReclaimPreview}>
                        <p className={s.noteText}>
                            {preview.dryRun
                                ? `Plan: ${reclaimableCount} of ${preview.rowsProcessed} row(s) will be reclaimed for ${this.formatCurrency(preview.totalPlanned)}.`
                                : `Result: reclaimed ${this.formatCurrency(preview.totalReclaimed)} across ${preview.rowsReclaimed} row(s); ${preview.rowsSkipped} skipped, ${preview.rowsFailed} failed.`}
                            {preview.merchantPayoutsPaused ? ' Merchant automatic payouts were paused (insufficient balance).' : ''}
                        </p>
                        <div className={s.pendingTipsBreakdownTableWrap}>
                            <table className={s.pendingTipsBreakdownTable}>
                                <thead>
                                    <tr>
                                        <th>Transfer</th>
                                        <th>Employee</th>
                                        <th>Work period</th>
                                        <th>Amount</th>
                                        <th>Prior status</th>
                                        <th>Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map(row => (
                                        <tr key={row.transferId}>
                                            <td>#{row.transferId}</td>
                                            <td>{row.username || row.userId}</td>
                                            <td>{row.workPeriodId ?? '—'}</td>
                                            <td>{this.formatCurrency(row.amount)}</td>
                                            <td>{row.previousStatus || '—'}</td>
                                            <td title={row.message}>{this.describeForceReclaimOutcome(row.outcome)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : null}

                {auditOpen ? (
                    <div className={s.forceReclaimAudit}>
                        <div className={s.reserveControlsHeader}>Force reclaim history</div>
                        {!audit ? <p className={s.noteText}>Loading…</p> : null}
                        {audit && audit.length === 0 ? <p className={s.noteText}>No force reclaims recorded for this merchant.</p> : null}
                        {audit && audit.length > 0 ? (
                            <div className={s.pendingTipsBreakdownTableWrap}>
                                <table className={s.pendingTipsBreakdownTable}>
                                    <thead>
                                        <tr>
                                            <th>When (UTC)</th>
                                            <th>Admin</th>
                                            <th>Reclaimed</th>
                                            <th>Rows</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {audit.map(rec => (
                                            <tr key={rec.auditId}>
                                                <td>{rec.attemptedAtUtc ? new Date(rec.attemptedAtUtc).toLocaleString() : '—'}</td>
                                                <td>{rec.adminUserName || '—'}</td>
                                                <td>{this.formatCurrency(rec.totalReclaimed)}</td>
                                                <td>{rec.rowsReclaimed} ok / {rec.rowsSkipped} skipped / {rec.rowsFailed} failed</td>
                                                <td>{rec.reason || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Widget>
        );
    }

    renderForceReclaimConfirmModal() {
        if (!this.state.forceReclaimConfirmOpen) {
            return null;
        }

        const { forceReclaimPreview: preview, forceReclaimRunning: running } = this.state;
        const scope = this.buildForceReclaimScope();
        const reclaimableCount = preview
            ? preview.rows.filter(r => r.outcome === 'would_reclaim').length
            : 0;

        return (
            <Modal
                isOpen
                toggle={this.closeForceReclaimConfirm}
                backdrop={running ? 'static' : true}
                className={s.confirmModal}
            >
                <ModalHeader
                    toggle={running ? undefined : this.closeForceReclaimConfirm}
                    className={s.confirmModalHeader}
                >
                    Force reclaim tips to platform
                </ModalHeader>
                <ModalBody className={s.confirmModalBody}>
                    <p className={s.confirmIntro}>
                        This <strong>bypasses the normal tip payout guards</strong> and immediately reverses
                        {' '}{preview ? this.formatCurrency(preview.totalPlanned) : ''} across {reclaimableCount} transfer row(s)
                        from the merchant Stripe account back to the platform.
                    </p>
                    <ul className={s.releaseReserveList}>
                        <li>Held, skipped, and failed rows in scope will be reclaimed.</li>
                        <li>Tips already paid to employees are never clawed back.</li>
                        <li>Reclaimed rows are frozen as <code>admin_reclaimed</code> — no automatic employee payout will follow.</li>
                        <li>This run is recorded in the audit log with your identity and reason.</li>
                    </ul>
                    <p className={s.confirmFeeNote}>
                        Reason: <em>{scope.reason}</em>
                    </p>
                </ModalBody>
                <ModalFooter className={s.confirmModalFooter}>
                    <div className={s.confirmModalActions}>
                        <Button
                            color="secondary"
                            type="button"
                            onClick={this.closeForceReclaimConfirm}
                            disabled={running}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            onClick={this.executeForceReclaim}
                            disabled={running}
                        >
                            {running ? 'Reclaiming…' : `Force reclaim ${preview ? this.formatCurrency(preview.totalPlanned) : ''}`}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    render() {
        if (!this.props.identityRolesLoaded) {
            return (
                <Widget title="Payout" className={s.payoutWidget}>
                    <p className={s.noteText}>Loading access…</p>
                </Widget>
            );
        }

        if (!this.canAccessPayoutPage()) {
            return <Redirect to="/admin/products" />;
        }

        if (!this.props.merchant?.MerchantId) {
            return (
                <Widget title="Payout" className={s.payoutWidget}>
                    <p className={s.noteText}>Merchant information is unavailable for this session.</p>
                </Widget>
            );
        }

        if (this.props.canViewTipSummaryReadOnly) {
            return this.renderReadOnlyTipsDashboard();
        }

        return (
            <div className={s.page}>
                {this.renderPlatformFundingSummary()}

                <Widget title="Payout" className={s.payoutWidget}>
                    {this.renderPreviewPanel()}
                </Widget>

                {this.renderMerchantConfigurationSection()}
                {this.renderAdminForceReclaimSection()}
                {this.renderForceReclaimConfirmModal()}
                {this.renderReleaseReserveModal()}
                {this.renderPayoutConfirmModal()}
                {this.renderPayoutSuccessModal()}
                {this.renderTaxCollectedResetConfirmModal()}
                {this.renderPayTipsConfirmModal()}
                {this.renderMarkAllPaidConfirmModal()}
                {this.renderPayRecipientConfirmModal()}
                {this.renderEmployeeWorkPeriodModal()}
                {this.renderPendingTipsBreakdownModal()}
                {this.renderPaidToManagerBreakdownModal()}
                {this.renderReleasableTipsBreakdownModal()}
            </div>
        );
    }
}

function hasReserveAmount(value) {
    return toNumber(value) > 0;
}

function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function parseAmountInput(rawValue) {
    const trimmed = String(rawValue ?? '').trim();
    if (!trimmed || trimmed === '.' || trimmed === '-') {
        return null;
    }

    const normalized = trimmed.replace(/[$,\s]/g, '');
    if (!normalized || normalized === '.' || normalized === '-') {
        return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

function calculatePlatformInstantFee(amount, preview) {
    if (!amount || amount <= 0) return 0;
    const feePercent = toNumber(preview?.instantPayoutFeePercent, 2);
    const feeFixed = toNumber(preview?.instantPayoutFeeFixed, 0);
    const percentFee = Math.round(amount * feePercent) / 100;
    const totalFee = percentFee + (feeFixed > 0 ? feeFixed : 0);
    return Math.round(totalFee * 100) / 100;
}

function mapStateToProps(state) {
    const authRoleState = buildAuthRoleState(state.auth);

    return {
        merchant: state.auth.merchant || null,
        storeDetails: state.settings.storeDetails || null,
        preview: state.merchantPayout.preview || null,
        previewLoading: state.merchantPayout.previewLoading || false,
        previewError: state.merchantPayout.previewError || '',
        reserveSaving: state.merchantPayout.reserveSaving || false,
        reserveLoading: state.merchantPayout.reserveLoading || false,
        reserveLoadError: state.merchantPayout.reserveLoadError || '',
        reserveConfig: state.merchantPayout.reserveConfig || null,
        payoutCreating: state.merchantPayout.payoutCreating || false,
        payoutError: state.merchantPayout.payoutError || '',
        tipFundingSummary: state.users.tipFundingSummary || null,
        tipFundingSummaryLoading: state.users.tipFundingSummaryLoading || false,
        tipFundingSummaryError: state.users.tipFundingSummaryError || '',
        identityRolesLoaded: authRoleState.identityRolesLoaded,
        isRebelityAdmin: authRoleState.isRebelityAdmin,
        identityRoles: authRoleState.identityRoles,
        useSimplifiedPayoutDashboard: shouldUseSimplifiedPayoutDashboard(authRoleState),
        canMerchantBankPayout: canMerchantBankPayout(authRoleState),
        canViewMerchantBalanceDetails: canViewMerchantBalanceDetailsFn(authRoleState),
        canConfigureReserve: canConfigureMerchantReserve(authRoleState),
        canViewTipPayoutSettings: canViewTipPayoutSettingsFn(authRoleState),
        canViewTipSummaryReadOnly: canViewTipSummaryReadOnlyFn(authRoleState),
        canConfigureTipPayoutRecipient: canConfigureTipPayoutRecipientFn(authRoleState),
        canExecuteTipPayoutToRecipient: canExecuteTipPayoutToRecipientFn(authRoleState),
        tipPayoutSettings: state.merchantPayout.tipPayoutSettings || null,
        tipPayoutSettingsLoading: state.merchantPayout.tipPayoutSettingsLoading || false,
        tipPayoutSettingsDateRangeKey: state.merchantPayout.tipPayoutSettingsDateRangeKey || 'all',
        tipPayoutSettingsError: state.merchantPayout.tipPayoutSettingsError || '',
        tipPayoutSettingsSaving: state.merchantPayout.tipPayoutSettingsSaving || false,
        tipPayoutPaying: state.merchantPayout.tipPayoutPaying || false,
        tipPayoutPayError: state.merchantPayout.tipPayoutPayError || '',
        tipPayoutMarkAllLoading: state.merchantPayout.tipPayoutMarkAllLoading || false,
        tipPayoutMarkAllError: state.merchantPayout.tipPayoutMarkAllError || '',
        tipPayoutDirectRecipients: state.merchantPayout.tipPayoutDirectRecipients,
        tipPayoutDirectRecipientsLoading: state.merchantPayout.tipPayoutDirectRecipientsLoading || false,
        tipPayoutDirectRecipientsError: state.merchantPayout.tipPayoutDirectRecipientsError || '',
        tipPayoutDirectPaying: state.merchantPayout.tipPayoutDirectPaying || false,
        tipPayoutDirectPayError: state.merchantPayout.tipPayoutDirectPayError || '',
        tipPayoutDirectPayingUserId: state.merchantPayout.tipPayoutDirectPayingUserId,
        tipPayoutDesignatedRecipientsLoading: state.merchantPayout.tipPayoutDesignatedRecipientsLoading || false,
        tipPayoutDesignatedRecipientsError: state.merchantPayout.tipPayoutDesignatedRecipientsError || '',
        tipPayoutPendingBreakdownLoading: state.merchantPayout.tipPayoutPendingBreakdownLoading || false,
        tipPayoutPendingBreakdownError: state.merchantPayout.tipPayoutPendingBreakdownError || '',
        tipPayoutPaidToManagerBreakdownLoading:
            state.merchantPayout.tipPayoutPaidToManagerBreakdownLoading || false,
        tipPayoutPaidToManagerBreakdownError:
            state.merchantPayout.tipPayoutPaidToManagerBreakdownError || '',
        tipPayoutReleasableBreakdownLoading: state.merchantPayout.tipPayoutReleasableBreakdownLoading || false,
        tipPayoutReleasableBreakdownError: state.merchantPayout.tipPayoutReleasableBreakdownError || '',
        recentSuccessfulTipPayouts: state.merchantPayout.recentSuccessfulTipPayouts || [],
        recentSuccessfulTipPayoutsLoading: state.merchantPayout.recentSuccessfulTipPayoutsLoading || false,
        recentSuccessfulTipPayoutsError: state.merchantPayout.recentSuccessfulTipPayoutsError || '',
        recentMerchantBankPayouts: state.merchantPayout.recentMerchantBankPayouts || [],
        recentMerchantBankPayoutsLoading: state.merchantPayout.recentMerchantBankPayoutsLoading || false,
        recentMerchantBankPayoutsError: state.merchantPayout.recentMerchantBankPayoutsError || '',
        tipPayoutSummaryCache: state.merchantPayout.tipPayoutSummaryCache || {},
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(PayoutSettings)));
